/* eslint-disable @typescript-eslint/no-unused-vars */
import { Subject, Observable } from 'rxjs';

import {
  BaseMeetingEvent,
  MeetingService as IMeetingService,
  MeetingServiceOptions,
  MeetingJoinOptions,
  MeetingConnectionState,
  MeetingRoomState,
  MeetingTrackType,
  MeetingParticipant,
  MeetingTrack,
} from '../interfaces/meeting-service.interface';
import { Logger } from './logger.service';
import {
  fetchNoiseSuppressionPreference,
  persistNoiseSuppressionPreference,
} from '../utils/noise-suppression-preference.util';
import { MeetingService } from '../../apps/meeting/services/MeetingService';
import { JitsiEventMapper } from '../utils/jitsi/event-mapper';
import { JitsiConferenceEvents, JitsiConnectionEvents } from 'nexgen-types/lib-jitsi';
import { getJitsiConnectionConfig } from '../utils/jitsi/helper-methods';
import { ScreenShareManager } from '../utils/jitsi/screen-share-manager';
import {
  CONFERENCE_LEAVE_REASONS,
  JITSI_DEFAULT_RESOLUTION,
  JITSI_FAST_START_VIDEO_CONSTRAINTS,
  JITSI_MEET_CONFIG,
  JITSI_OPTIMAL_AUDIO_CONSTRAINTS,
  JITSI_OPTIMAL_BITRATE_CONFIG,
  JITSI_OPTIMAL_VIDEO_CONSTRAINTS,
  JITSI_OPTIMAL_VIDEO_QUALITY,
  JITSI_VIDEO_QUALITY_UPGRADE_DELAY_MS,
} from '../../utils/constants';
import { useRecordingStore, useWorkplaceStore } from '../../host/stores';
import { SPEAKING_SORT_INTERVAL_MS, useMeetingStore } from '../../host/stores/meeting.store';
import {
  VirtualBackgroundOptions,
  VIRTUAL_BACKGROUND_TYPE,
  BlurPreset,
} from '../../apps/meeting/types';
import { useLiveCaptionsStore } from '../../host/stores/liveCaptions.store';
import { useNotificationStore } from '../../host/stores/notification.store';
import { LocalTrackService } from './local-track.service';
import { debounce } from '../utils/debounce';
import { isVirtualAudioDevice } from '../utils/virtual-audio-device.util';
import { NoiseSuppressionEffect } from '../../utils/noise-suppression/NoiseSuppressionEffect';

export declare let JitsiMeetJS: any;

interface LeaveMeetingParams {
  killConnection?: boolean;
  leaveReason?: string;
}

interface SpeakerAudioEvent {
  smoothedLevel: number;
  lastActive: number;
  isSpeaking: boolean;
  speakingStartedAt: number | null;
}

export interface JitsiMeetingEvent extends BaseMeetingEvent {
  type: string;
  payload?: any;
  state?: MeetingConnectionState | MeetingRoomState;
  roomName?: string;
  trackType?: MeetingTrackType;
  action?: string;
  participantId?: string;
  track?: any;
}

export class JitsiMeetingService implements IMeetingService<JitsiMeetingEvent> {
  private connection: any = null;
  private conference: any = null;
  private localTrackService: LocalTrackService = LocalTrackService.instance;
  private localTracks: any[] = [];
  private conferenceOptions: any;

  private eventSubject = new Subject<JitsiMeetingEvent>();
  private audioLevelSubject = new Subject<{ participantId: string; audioLevel: number }>();
  private speakingState = new Map<string, SpeakerAudioEvent>();
  private isRoomSwitching = false;
  // @ts-expect-error declared but unused — retained for room-switch tracking
  private pendingRoomJid: string | null = null;
  private previousRoomJid: string | null = null;
  private SMOOTHING = 0.3;
  private START_THRESHOLD = 0.075;
  private STOP_THRESHOLD = 0.03;
  private DECAY_TIME = 1000;

  private eventMapper: JitsiEventMapper;
  private screenShareManager: ScreenShareManager;
  private cleanupInterval: any;
  private lastPeriodicSortAt = 0;
  private audioLevelSubscription: any;
  private deviceChangeHandler: (() => void) | null = null;
  // null = unknown (never emitted yet); boolean = last emitted state
  private cameraIsOn: boolean | null = null;
  // NGE-841: same tracking for the local mic, mirroring cameraIsOn.
  private micIsOn: boolean | null = null;
  // Set when the camera fails to start during initLocalTracks (e.g. gum.general in Firefox)
  private cameraUnavailable = false;
  // Resolves when the current initLocalTracks call completes (success or failure). Used by
  // toggleTrack to avoid a race where POLICY-EFFECT fires between conference.join() and
  // initLocalTracks adding the forceVideoOn track — causing a duplicate camera stream.
  private _initTracksPromise: Promise<void> | null = null;

  /** Exposed for activity-clean-exit.service.ts — whether a final camera "off" beacon is needed on unload. */
  isCameraOn(): boolean {
    return this.cameraIsOn === true;
  }

  /** Exposed for activity-clean-exit.service.ts — whether a final mic "off" beacon is needed on unload. */
  isMicOn(): boolean {
    return this.micIsOn === true;
  }

  private onTrackAdded = (track: any) => {
    this.eventMapper.handleTrackAdded(track);
    // Mid-meeting camera enable: track created for unmuted video while camera was off
    if (
      track.isLocal() &&
      track.getType() === 'video' &&
      !track.isMuted() &&
      this.cameraIsOn !== true
    ) {
      this.handleCameraActivityChange('on');
    }
  };

  private onTrackRemoved = (track: any) => {
    this.eventMapper.handleTrackRemoved(track);
  };

  private onConferenceJoined = () => {
    this.eventMapper.handleConferenceJoined();
  };

  private onUserJoined = (id: string, user: any) => {
    this.eventMapper.handleUserJoined(id, user);
    this.trackParticipantActivity(id, 'joined');
  };

  // NGE-681: keep each participant's moderator flag in sync. JaaS may grant the
  // moderator role slightly after USER_JOINED, so reflect role changes in the store
  // for the auditorium layout (presenter stage tiles vs. listen-only audience).
  private onUserRoleChanged = (id: string, role: string) => {
    const isModerator = role === 'moderator';
    useMeetingStore.setState((state) => {
      const participant = state.jitsiParticipants[id];
      if (!participant || participant.isModerator === isModerator) return state;
      return {
        jitsiParticipants: {
          ...state.jitsiParticipants,
          [id]: { ...participant, isModerator },
        },
      };
    });
  };

  private onUserLeft = (id: string, user: any) => {
    this.eventMapper.handleUserLeft(id, user);
    this.trackParticipantActivity(id, 'left');
    this.speakingState.delete(id);
    const participant = this.conference?.getParticipantById(id);
    if (participant) {
      participant.getTracks().forEach((track: any) => {
        track.detach();
        track.dispose().catch(() => {});
      });
    }
  };

  private onTrackMuteChanged = (track: any, actor: any) => {
    this.eventMapper.handleTrackMuteChanged(track);
    // Camera activity tracking for local video tracks only — manual toggle while in meeting
    if (track.getType() === 'video' && track.isLocal()) {
      if (track.isMuted() && this.cameraIsOn) {
        this.handleCameraActivityChange('off');
      } else if (!track.isMuted() && !this.cameraIsOn) {
        this.handleCameraActivityChange('on');
      }
    }
    // Track audio mute/unmute events for all participants
    if (track.getType() === 'audio') {
      const participantId = track.getParticipantId() || this.getMyUserId();
      if (participantId) {
        this.trackParticipantActivity(participantId, track.isMuted() ? 'muted' : 'unmuted');
      }
    }
    // NGE-841: mic activity tracking for the local audio track only — mirrors
    // the camera block above, manual toggle while in meeting.
    if (track.getType() === 'audio' && track.isLocal()) {
      if (track.isMuted() && this.micIsOn) {
        this.handleMicActivityChange('off');
      } else if (!track.isMuted() && !this.micIsOn) {
        this.handleMicActivityChange('on');
      }
    }
    // Notify the local user when they are muted remotely by another participant
    if (track.isLocal() && track.getType() === 'audio' && actor != null && track.isMuted()) {
      const myId = this.getMyUserId();
      if (myId) {
        this.eventSubject.next(
          this.eventMapper.createTrackMuted(MeetingTrackType.AUDIO, myId, track)
        );
        useNotificationStore.getState().showSnackbar('You have been muted.', 'info');
      }
    }
  };

  public get events$(): Observable<JitsiMeetingEvent> {
    return this.eventSubject.asObservable();
  }

  public currentResolution: number = JITSI_DEFAULT_RESOLUTION;

  private vbOptions: VirtualBackgroundOptions = this.loadVbStateFromStorage();

  private effectFactory?: (state: VirtualBackgroundOptions) => Promise<any>;

  private static NS_STATE_KEY = 'meeting-ns-state';
  // Seeded synchronously from localStorage so the toggle renders instantly; the authoritative
  // value comes from the server preference via hydrateNoiseSuppressionPreference().
  private nsEnabled: boolean = JitsiMeetingService.loadNsStateFromStorage();
  private nsEffect: NoiseSuppressionEffect | null = null;
  private _nsApplying = false;

  constructor(
    conferenceOptions: any = { openBridgeChannel: true },
    effectFactory?: (state: VirtualBackgroundOptions) => Promise<any>
  ) {
    this.conferenceOptions = conferenceOptions;
    this.effectFactory = effectFactory;

    try {
      JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.ERROR);
      JitsiMeetJS.init(JITSI_MEET_CONFIG);
    } catch (error) {
      Logger.error('[JitsiMeetingService] Failed to initialize JitsiMeetJS:', error);
    }

    this.conferenceOptions = conferenceOptions;
    this.eventMapper = new JitsiEventMapper(
      this.eventSubject,
      this.getTrackTypeFromJitsiTrack.bind(this)
    );
    this.screenShareManager = new ScreenShareManager(
      this.eventSubject,
      this.eventMapper,
      this.getTrackTypeFromJitsiTrack.bind(this),
      this.localTrackService.tracks
    );
  }

  async initialize(options: MeetingServiceOptions = {}, token?: string): Promise<void> {
    try {
      Logger.debug('[JitsiMeetingService] Initializing connection...');
      Logger.debug('[JitsiMeetingService] Room ID:', options.roomConfig?.roomId);
      Logger.debug('[JitsiMeetingService] Has JWT token:', !!token);

      const connectionConfig = getJitsiConnectionConfig(options.roomConfig?.roomId);
      this.connection = new JitsiMeetJS.JitsiConnection(null, token, connectionConfig);
      this.listenToConnectionEvents();
      await this.connection.connect();

      Logger.debug('[JitsiMeetingService] ✅ Connection initialized successfully');
    } catch (error) {
      Logger.error('[JitsiMeetingService] ❌ Failed to initialize connection:', error);
      throw error;
    }
  }

  // Serializes every call that mutates this.conference/this.connection (joinMeeting,
  // switchToRoom). Without this, a page-reload bootstrap join and a breakout-room
  // switchToRoom (triggered independently by FloatingMeetingView once it learns the
  // user belongs to an active breakout room) can both call initJitsiConference()
  // concurrently — whichever resolves last silently wins and overwrites this.conference,
  // orphaning the other conference/its tracks and leaving the participant's tile gone.
  private operationQueue: Promise<void> = Promise.resolve();

  private runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    const result = this.operationQueue.then(fn, fn);
    this.operationQueue = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  async joinMeeting(roomId: string, options: MeetingJoinOptions): Promise<void> {
    return this.runExclusive(() => this.joinMeetingInternal(roomId, options));
  }

  private async joinMeetingInternal(roomId: string, options: MeetingJoinOptions): Promise<void> {
    try {
      Logger.debug('[JitsiMeetingService] Attempting to join meeting...');
      Logger.debug('[JitsiMeetingService] Room ID:', roomId);
      Logger.debug('[JitsiMeetingService] Has JWT token:', !!options.token);

      if (options.shouldAbort?.()) {
        Logger.debug('[JitsiMeetingService] ⏭️ Join superseded before start, skipping:', roomId);
        return;
      }

      if (roomId === this.conference?.getName()) {
        Logger.debug('[JitsiMeetingService] Already in this conference, skipping join');
        return;
      }

      if (!this.connection) {
        throw new Error('Connection not initialized. Please call initialize() first.');
      }

      const [roomIdentifier, domain] = roomId.split('@');

      if (!this.isRoomSwitching && this.conference?.getName() === roomIdentifier) {
        Logger.debug('[JitsiMeetingService] Already in this conference, skipping join');
        return;
      }

      const conferenceOptions = {
        ...this.conferenceOptions,
        ...(options.roomConfig ?? {}),
        openBridgeChannel: true,
        p2p: { enabled: false },
        ...(domain?.includes('breakout') && { customDomain: domain }),
      };

      Logger.debug('[JitsiMeetingService] Conference options:', conferenceOptions);

      this.eventMapper.setRoomName(roomIdentifier);
      this.eventMapper.setRoomState(MeetingRoomState.JOINING);
      this.conference = this.connection.initJitsiConference(roomIdentifier, conferenceOptions);

      this.listenToConferenceEvents();
      //this.initLocalTracks(options);
      this.conference.setDisplayName(useWorkplaceStore.getState().user.name);

      Logger.debug('[JitsiMeetingService] Calling conference.join()...');
      //await this.conference.join();
      const joined = await this.joinConferenceWithRetry(5, options.shouldAbort);
      if (!joined) {
        // Superseded mid-retry. Don't create local tracks or wire up device listeners for a
        // conference nobody is waiting on — the newer join builds its own.
        Logger.debug('[JitsiMeetingService] ⏭️ Join superseded during retry, abandoning:', roomId);
        return;
      }

      // Initialize local tracks after successful join
      await this.initLocalTracks(options);
      if (options.createLocalTracks !== false) {
        this.emitInitialCameraState();
        this.emitInitialMicState();
      }
      this.setupDeviceChangeListener();
      Logger.debug('[JitsiMeetingService] ✅ Successfully joined conference');
    } catch (error: any) {
      Logger.error('[JitsiMeetingService] ❌ Failed to join meeting:', error);
      Logger.error('[JitsiMeetingService] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });

      try {
        if (error.message.includes('A conference with the same name has already been created!')) {
          Logger.debug('[JitsiMeetingService] Retrying join after duplicate conference error...');
          await this.conference.join();
        }
      } catch (e) {
        Logger.error('[JitsiMeetingService] ❌ Retry failed:', e);
      }
      this.eventMapper.setRoomState(MeetingRoomState.ERROR);
      this.eventSubject.next(this.eventMapper.createConferenceLeft(error));
    }
  }

  /** Resolves true once joined, false if a newer room switch superseded this attempt. */
  private async joinConferenceWithRetry(
    maxRetries = 5,
    shouldAbort?: () => boolean
  ): Promise<boolean> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (shouldAbort?.()) return false;
      try {
        await this.conference.join();
        return true;
      } catch (error: any) {
        lastError = error;
        const errStr = (() => {
          try {
            return typeof error === 'string'
              ? error
              : JSON.stringify(error?.message ?? error?.reason ?? error);
          } catch {
            return String(error?.message ?? error);
          }
        })();
        console.warn(`[JitsiMeetingService] Join attempt ${attempt} failed:`, errStr);

        // Retryable: stale conference cleanup OR XMPP not yet ready after page reload
        // (Strophe "Not connected" + cascading moderator IQ errors).
        const isRetryable =
          errStr.includes('A conference with the same name has already been created') ||
          errStr.includes('Not connected') ||
          errStr.includes('session-invalid') ||
          errStr.includes('not-acceptable') ||
          errStr.includes('reservation-error') ||
          errStr.includes('querySelector is not a function');

        if (isRetryable && attempt < maxRetries) {
          const waitTime = 1000 * attempt;
          Logger.debug(`[JitsiMeetingService] Waiting ${waitTime}ms before retry...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
          continue;
        }

        // Non-retryable error or last attempt — propagate
        throw error;
      }
    }

    throw lastError;
  }

  getMyUserId = () => {
    if (!this.conference) return;
    return this.conference.myUserId();
  };

  /** NGE-681: current conference room identifier (or null if not joined). */
  getCurrentRoomName(): string | null {
    return this.conference?.getName?.() ?? null;
  }

  /**
   * NGE-681: handle broadcast (group) chat messages. We piggyback a small JSON
   * control message on Jitsi's group chat to signal auditorium lifecycle events
   * (e.g. the moderator ending the session) and re-dispatch as a window event.
   */
  private onBroadcastMessage = (_id: string, text: string) => {
    try {
      const msg = JSON.parse(text);
      if (msg?.ngType === 'AUDITORIUM_ENDED') {
        window.dispatchEvent(new CustomEvent('auditorium:ended'));
      }
    } catch {
      /* not a control message — ignore */
    }
  };

  /** NGE-681: moderator broadcasts that the auditorium has ended (all attendees leave). */
  broadcastAuditoriumEnded(): void {
    try {
      this.conference?.sendMessage(JSON.stringify({ ngType: 'AUDITORIUM_ENDED' }));
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to broadcast auditorium-ended:', error);
    }
  }

  /**
   * NGE-1078: the promoted participant's own client has to re-fetch its join token
   * before a new role takes effect in the live call. Until
   * `AuditoriumRoleChangedEvent` lands in nexgen-types, the promoting moderator
   * tells the target directly over a Jitsi endpoint message (data channel, not
   * chat, so nothing shows up in the chat panel); every client also polls
   * `GET /:pid/me/role` as the fallback for anyone who missed it.
   */
  sendAuditoriumRoleChanged(participantId: string, role: string): void {
    try {
      this.conference?.sendEndpointMessage?.(participantId, {
        ngType: 'AUDITORIUM_ROLE_CHANGED',
        role,
      });
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to send auditorium role change:', error);
    }
  }

  /**
   * NGE-1078: receive the moderator's role-change nudge and re-dispatch it as a
   * window event, mirroring how onBroadcastMessage surfaces auditorium lifecycle
   * control messages. Non-auditorium endpoint messages are ignored.
   */
  private onEndpointMessage = (_participant: any, payload: any) => {
    if (payload?.ngType !== 'AUDITORIUM_ROLE_CHANGED') return;
    window.dispatchEvent(
      new CustomEvent('auditorium:role-changed', { detail: { role: payload.role } })
    );
  };

  clearPersistedMuteState(): void {
    try {
      localStorage.removeItem(JitsiMeetingService.MUTE_STATE_KEY);
    } catch (error) {
      // Ignore localStorage errors
    }
  }

  resetMuteStateToUnmuted(): void {
    this.saveMuteStateToStorage({ isVideoMuted: false, isAudioMuted: false });
  }

  persistCurrentMuteState(): void {
    this.saveMuteStateToStorage(this.getLocalMuteState());
  }

  async leaveMeeting({
    killConnection = true,
    leaveReason,
  }: LeaveMeetingParams = {}): Promise<void> {
    this.teardownDeviceChangeListener();
    this.flushCameraMicOffOnLeave();

    try {
      const videoTrack = await this.getLocalVideoTrack();
      if (videoTrack) await videoTrack.setEffect(undefined);
    } catch (error) {
      Logger.error(error);
    }

    try {
      const audioTrack = this.getLocalAudioTrack();
      if (audioTrack) await audioTrack.setEffect(undefined);
    } catch (error) {
      Logger.error(error);
    }
    this.nsEffect?.stopEffect();
    this.nsEffect = null;

    // Use LocalTrackService to dispose tracks - preserve screen share if switching rooms
    const typesToKeep =
      leaveReason === CONFERENCE_LEAVE_REASONS.SWITCH_ROOM ? [MeetingTrackType.SCREEN] : [];
    await this.localTrackService.disposeTracks(typesToKeep);

    if (this.conference) {
      try {
        const {
          handleRecorderStateChanged,
          handleBreakoutRoomStateChanged,
          handleMoveToBreakoutRoom,
          handleConferenceError,
          handlePrivateMessageReceived,
        } = this.eventMapper;

        this.conference.off(JitsiConferenceEvents.TRACK_ADDED, this.onTrackAdded);
        this.conference.off(JitsiConferenceEvents.TRACK_REMOVED, this.onTrackRemoved);
        this.conference.off(JitsiConferenceEvents.TRACK_MUTE_CHANGED, this.onTrackMuteChanged);
        this.conference.off(JitsiConferenceEvents.CONFERENCE_JOINED, this.onConferenceJoined);
        this.conference.off(JitsiConferenceEvents.MESSAGE_RECEIVED, this.onBroadcastMessage);
        this.conference.off(
          JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
          this.onEndpointMessage
        );
        this.conference.off(JitsiConferenceEvents.USER_JOINED, this.onUserJoined);
        this.conference.off(JitsiConferenceEvents.USER_LEFT, this.onUserLeft);
        this.conference.off(JitsiConferenceEvents.USER_ROLE_CHANGED, this.onUserRoleChanged);
        this.conference.off(JitsiConferenceEvents.CONFERENCE_ERROR, handleConferenceError);
        this.conference.off(
          JitsiConferenceEvents.PRIVATE_MESSAGE_RECEIVED,
          handlePrivateMessageReceived
        );
        this.conference.off(
          JitsiConferenceEvents.RECORDER_STATE_CHANGED,
          handleRecorderStateChanged
        );
        this.conference.off(
          JitsiConferenceEvents.BREAKOUT_ROOMS_UPDATED,
          handleBreakoutRoomStateChanged
        );
        this.conference.off(
          JitsiConferenceEvents.BREAKOUT_ROOMS_MOVE_TO_ROOM,
          handleMoveToBreakoutRoom
        );

        this.cleanupAudioLevelHandlers();
        await this.conference.leave(leaveReason);
        this.eventSubject.next(this.eventMapper.createConferenceLeft());
      } catch (error) {
        Logger.error('Error leaving conference:', error);
        // setRoomState must be called before emitting so that getRoomState() returns
        // LEFT when the setupEventListeners subscription handler fires and updates
        // useMeetingStore.meetingRoomState. Without this, a failed conference.leave()
        // (e.g. after ICE restart failure) leaves meetingRoomState stuck at JOINED,
        // which prevents GuestLobbyPage from triggering joinJitsiMeeting for the new room.
        this.eventMapper.setRoomState(MeetingRoomState.LEFT);
        this.eventSubject.next(this.eventMapper.createConferenceLeft(error));
      } finally {
        // Always transition to LEFT — ensures clean state regardless of leave() outcome.
        this.eventMapper.setRoomState(MeetingRoomState.LEFT);
        this.conference = null;
      }
    }

    if (this.connection && killConnection && leaveReason !== CONFERENCE_LEAVE_REASONS.SWITCH_ROOM) {
      try {
        this.connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_ESTABLISHED,
          this.eventMapper.handleConnectionEstablished
        );
        this.connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_FAILED,
          this.eventMapper.handleConnectionFailed
        );
        this.connection.removeEventListener(
          JitsiConnectionEvents.CONNECTION_DISCONNECTED,
          this.eventMapper.handleConnectionDisconnected
        );

        // Wait for the XMPP stream to actually close before returning so
        // 8x8 has freed the slot for this user before we (potentially) reload
        // and try to reconnect. Without this wait, connection.disconnect() is
        // fire-and-forget and the new connection can race the cleanup, producing
        // "Not connected" / session-invalid errors.
        await new Promise<void>((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            try {
              this.connection?.removeEventListener(
                JitsiConnectionEvents.CONNECTION_DISCONNECTED,
                onDisconnected
              );
            } catch {
              /* noop */
            }
            resolve();
          };
          const onDisconnected = () => finish();

          try {
            this.connection!.addEventListener(
              JitsiConnectionEvents.CONNECTION_DISCONNECTED,
              onDisconnected
            );
            this.connection!.disconnect();
          } catch (err) {
            Logger.error('Error during disconnect await:', err);
            finish();
            return;
          }

          // Safety timeout — never block the caller for more than 3s.
          setTimeout(finish, 3000);
        });

        this.eventMapper.setConnectionState(MeetingConnectionState.DISCONNECTED);
      } catch (error) {
        Logger.error('Error disconnecting connection:', error);
      } finally {
        this.connection = null;
      }
    }

    this.eventMapper.setRoomName('');
  }

  async initLocalTracks(options: MeetingJoinOptions): Promise<void> {
    let finishInit!: () => void;
    this._initTracksPromise = new Promise<void>((resolve) => {
      finishInit = resolve;
    });
    try {
      this.cameraUnavailable = false;
      if (options.createLocalTracks !== false) {
        const MAX_RETRIES = 1;
        const RETRY_DELAY_MS = 1000;
        let lastError: any;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          let tempTracks: any[] = [];
          try {
            let trackTypes: string[] = options.tracks?.types || ['audio', 'video'];

            // Read saved mute state
            // For VIDEO: Skip creation when muted
            // For AUDIO: Always create but mute after (needed for transcription)
            const savedMuteState = this.loadMuteStateFromStorage();

            // Always On Video policy overrides the user's saved camera-off preference.
            // Without this, the video track is skipped during initLocalTracks and has to
            // be created post-join via toggleTrack, causing a visible delay in the tile.
            if (options.forceVideoOn) {
              savedMuteState.isVideoMuted = false;
            }

            const skippedVideo = savedMuteState.isVideoMuted && trackTypes.includes('video');
            const shouldMuteAudio = savedMuteState.isAudioMuted; // Don't skip, just mute after creation

            if (skippedVideo) {
              trackTypes = trackTypes.filter((t: string) => t !== 'video');
            }
            // Audio track is always created (even if it will be muted) - needed for transcription

            const hasVideo = trackTypes.some((type) => type === 'video');
            let videoFallback = false;
            if (trackTypes.length > 0) {
              const fastStartConstraints = hasVideo
                ? {
                    audio: options.tracks?.constraints?.audio,
                    video: { ...JITSI_FAST_START_VIDEO_CONSTRAINTS },
                  }
                : options.tracks?.constraints;
              const trackOptions = createOptimizedTrackOptions(trackTypes, fastStartConstraints);
              try {
                this.localTracks = await JitsiMeetJS.createLocalTracks(trackOptions);
              } catch (createError) {
                if (hasVideo) {
                  // Camera unavailable or permission denied — fall back to audio-only so the
                  // user can still participate in the call with their microphone.
                  Logger.warn(
                    '[JitsiMeetingService] Camera unavailable, falling back to audio-only:',
                    createError
                  );
                  const audioOnlyOptions = createOptimizedTrackOptions(
                    ['audio'],
                    options.tracks?.constraints
                  );
                  this.localTracks = await JitsiMeetJS.createLocalTracks(audioOnlyOptions);
                  videoFallback = true;
                  this.cameraUnavailable = true;
                  // Persist video-muted state immediately so subsequent calls to
                  // getLocalMuteState() (e.g. in setCurrentTrackStatus / switchRoom)
                  // reflect the unavailable camera rather than a stale "unmuted" value.
                  const fallbackState = this.loadMuteStateFromStorage();
                  fallbackState.isVideoMuted = true;
                  this.saveMuteStateToStorage(fallbackState);
                } else {
                  throw createError;
                }
              }
              this.localTrackService.addTracks(this.localTracks);
            } else {
              this.localTracks = [];
            }
            tempTracks = this.localTracks;
            const effectivelySkippedVideo = skippedVideo || videoFallback;

            for (const track of this.localTrackService.tracks) {
              if (track.getType() === 'video' && this.conference) {
                this.conference.setSenderVideoConstraint(JITSI_DEFAULT_RESOLUTION);
              }
              try {
                await this.conference.addTrack(track);
              } catch (error) {
                Logger.warn('[JitsiMeetingService] Failed to add track to conference:', error);
                // An unpublished track emits no TRACK_ADDED, so the tile never gets a video
                // slot while the controls still read the saved "unmuted" state — camera icon
                // on, no video. Retry once (the usual cause is the device still settling from
                // the previous screen), and if it still fails record the camera as off so the
                // stored state matches reality, the same discipline the audio-only fallback
                // above follows.
                const isVideoTrack = track.getType() === 'video';
                let republished = false;
                try {
                  await new Promise((resolve) => setTimeout(resolve, 300));
                  await this.conference.addTrack(track);
                  republished = true;
                  Logger.info('[JitsiMeetingService] Track published on retry');
                } catch (retryError) {
                  Logger.error(
                    '[JitsiMeetingService] Track could not be published after retry:',
                    retryError
                  );
                }
                if (!republished && isVideoTrack) {
                  const unpublishedState = this.loadMuteStateFromStorage();
                  unpublishedState.isVideoMuted = true;
                  this.saveMuteStateToStorage(unpublishedState);
                  this.cameraUnavailable = true;
                }
              }
            }

            // Mute audio track immediately if user's saved state was muted
            // Track exists (for transcription) but is muted (no audio transmitted)
            const myUserId = this.getMyUserId();
            if (shouldMuteAudio) {
              const audioTrack = this.localTracks.find((t: any) => t.getType() === 'audio');
              if (audioTrack) {
                await audioTrack.mute();
                Logger.debug('[JitsiMeetingService] Audio track created but muted (saved state)');
                // Emit mute event so UI shows muted state
                this.eventSubject.next(
                  this.eventMapper.createTrackMuted(MeetingTrackType.AUDIO, myUserId, audioTrack)
                );
              }
            }

            if (effectivelySkippedVideo) {
              const user = useWorkplaceStore.getState().user;
              useMeetingStore.setState((state) => {
                const existing = state.jitsiParticipants[myUserId];
                if (existing) return state;
                // NGE-681: seed the local moderator flag from the known auditorium role.
                const auditoriumRole = state.auditoriumRole;
                const localIsModerator = auditoriumRole ? auditoriumRole !== 'audience' : undefined;
                return {
                  jitsiParticipants: {
                    ...state.jitsiParticipants,
                    [myUserId]: {
                      id: myUserId,
                      displayName: user?.name ?? 'Me',
                      isLocal: true,
                      userPid: user?.pid,
                      avatarUrl: user?.avatar_url ?? '',
                      avatarThumbnailUrl: user?.avatar_thumbnail_url ?? undefined,
                      isModerator: localIsModerator,
                    },
                  },
                };
              });
            }

            // Emit mute event for skipped video track so UI stays in sync
            if (effectivelySkippedVideo) {
              this.eventSubject.next(
                this.eventMapper.createTrackMuted(MeetingTrackType.VIDEO, myUserId, null)
              );
            }

            // Notify MeetingService that local tracks are ready
            // This resolves the race condition for late joiners who start transcription
            // before their local tracks finish initializing
            try {
              MeetingService.instance?.notifyLocalTracksReady();
            } catch (error) {
              Logger.error('[JitsiMeetingService] Error notifying tracks ready:', error);
            }

            if (this.conference) {
              this.conference.setReceiverVideoConstraint(JITSI_DEFAULT_RESOLUTION);
            }

            void this.applyVirtualBackgroundToLocalVideo();
            await this.applySavedDevicePreferences();

            // Re-apply persisted noise suppression AFTER device preferences settle. Doing it
            // earlier caused a double init: NS applied on the default mic, then a saved-device
            // switch replaced the track and re-applied NS. Lazy + idempotent, so if the device
            // switch above already applied it, this is a no-op; otherwise it applies once here.
            //
            // Hydrate from the server preference first so the setting is honoured even when the
            // local cache is cold (cleared storage, new browser) and the user never opened
            // Device Settings this session. The result is memoised per page session.
            await this.hydrateNoiseSuppressionPreference();
            if (this.nsEnabled) {
              await this.applyNoiseSuppression();
            }

            if (hasVideo && !videoFallback) {
              setTimeout(() => {
                void this.upgradeVideoQuality(
                  JITSI_OPTIMAL_VIDEO_CONSTRAINTS,
                  JITSI_DEFAULT_RESOLUTION
                );
              }, JITSI_VIDEO_QUALITY_UPGRADE_DELAY_MS);
            }

            return; // success — exit retry loop
          } catch (error) {
            lastError = error;
            for (const track of tempTracks) {
              this.localTrackService.removeTrack(track);
              try {
                await track.dispose();
              } catch (disposeError) {
                Logger.error('[JitsiMeetingService] Error disposing partial track:', disposeError);
              }
            }

            if (attempt < MAX_RETRIES) {
              Logger.warn(
                `[JitsiMeetingService] Track init failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying in ${RETRY_DELAY_MS}ms...`,
                error
              );
              await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
            }
          }
        }

        // All attempts exhausted — emit error so the UI can surface it
        this.eventSubject.next(this.eventMapper.createLocalTracksError(lastError));
      }
    } finally {
      finishInit();
      this._initTracksPromise = null;
    }
  }

  listenToConnectionEvents(): void {
    if (!this.connection) return;
    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_ESTABLISHED,
      this.eventMapper.handleConnectionEstablished
    );
    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_FAILED,
      this.eventMapper.handleConnectionFailed
    );
    this.connection.addEventListener(
      JitsiConnectionEvents.CONNECTION_DISCONNECTED,
      this.eventMapper.handleConnectionDisconnected
    );
  }

  listenToConferenceEvents(): void {
    if (!this.conference) return;
    const events = JitsiMeetJS.events.conference;

    this.conference.on(JitsiConferenceEvents.TRACK_ADDED, this.onTrackAdded);
    this.conference.on(JitsiConferenceEvents.TRACK_REMOVED, this.onTrackRemoved);
    this.conference.on(
      JitsiConferenceEvents.PRIVATE_MESSAGE_RECEIVED,
      this.eventMapper.handlePrivateMessageReceived
    );

    this.conference.on(JitsiConferenceEvents.TRACK_MUTE_CHANGED, this.onTrackMuteChanged);
    this.conference.on(JitsiConferenceEvents.CONFERENCE_JOINED, this.onConferenceJoined);
    this.conference.on(JitsiConferenceEvents.USER_JOINED, this.onUserJoined);
    this.conference.on(JitsiConferenceEvents.USER_LEFT, this.onUserLeft);
    this.conference.on(JitsiConferenceEvents.USER_ROLE_CHANGED, this.onUserRoleChanged);
    // NGE-681: broadcast control messages (e.g. moderator ending the auditorium).
    this.conference.on(JitsiConferenceEvents.MESSAGE_RECEIVED, this.onBroadcastMessage);
    // NGE-1078: targeted role-change nudge from the moderator (data channel).
    this.conference.on(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, this.onEndpointMessage);
    this.conference.on(
      JitsiConferenceEvents.TRACK_AUDIO_LEVEL_CHANGED,
      (participantId: string, audioLevel: number) => {
        this.audioLevelSubject.next({ participantId, audioLevel });
      }
    );
    this.conference.on(
      JitsiConferenceEvents.RECORDER_STATE_CHANGED,
      this.eventMapper.handleRecorderStateChanged
    );
    this.conference.on(
      events.BREAKOUT_ROOMS_UPDATED,
      this.eventMapper.handleBreakoutRoomStateChanged
    );
    this.conference.on(
      events.BREAKOUT_ROOMS_MOVE_TO_ROOM,
      this.eventMapper.handleMoveToBreakoutRoom
    );
    // Connection quality events
    const connectionQualityEvents = JitsiMeetJS.events.connectionQuality;
    if (connectionQualityEvents) {
      const updateConnectionQuality = (participantId: string, stats: any) => {
        const connectionQuality = stats?.connectionQuality;
        if (typeof connectionQuality !== 'number') return;

        // Read current state to avoid unnecessary updates when nothing changes
        const { jitsiParticipants } = useMeetingStore.getState();
        const participant = jitsiParticipants[participantId];

        if (!participant) {
          // No participant to update; avoid calling setState
          return;
        }

        if (participant.connectionQuality === connectionQuality) {
          // Connection quality unchanged; avoid calling setState
          return;
        }

        useMeetingStore.setState((state) => ({
          jitsiParticipants: {
            ...state.jitsiParticipants,
            [participantId]: {
              ...state.jitsiParticipants[participantId],
              connectionQuality,
            },
          },
        }));
      };

      // Store handlers on the instance so they can be removed later with .off(...)
      const self = this as any;

      self._localConnectionQualityListener = (stats: any) => {
        const myId = this.getMyUserId();
        if (myId) updateConnectionQuality(myId, stats);
      };

      self._remoteConnectionQualityListener = (participantId: string, stats: any) => {
        updateConnectionQuality(participantId, stats);
      };

      this.conference.on(
        connectionQualityEvents.LOCAL_STATS_UPDATED,
        self._localConnectionQualityListener
      );
      this.conference.on(
        connectionQualityEvents.REMOTE_STATS_UPDATED,
        self._remoteConnectionQualityListener
      );
    }

    // Error handlers
    this.conference.on(JitsiConferenceEvents.CONFERENCE_ERROR, (error: any) =>
      this.eventMapper.handleConferenceError(error)
    );
    this.conference.on(events.CONFERENCE_JOIN_ERROR, (error: any) =>
      this.eventMapper.handleConferenceJoinError(error)
    );
    this.conference.on(events.TRACK_ERROR, (error: any) =>
      this.eventMapper.handleTrackError(error)
    );

    // Clean up any existing subscription and interval before creating new ones
    this.cleanupAudioLevelHandlers();

    // Store the subscription so we can unsubscribe later
    this.audioLevelSubscription = this.audioLevelSubject
      .pipe()
      .subscribe(({ participantId, audioLevel }) => {
        const cardElement = document.getElementById(`participant-card-${participantId}`);

        let state = this.speakingState.get(participantId);
        if (!state) {
          state = {
            smoothedLevel: audioLevel,
            lastActive: Date.now(),
            isSpeaking: false,
            speakingStartedAt: null,
          };
          this.speakingState.set(participantId, state);
        }

        state.smoothedLevel =
          state.smoothedLevel * (1 - this.SMOOTHING) + audioLevel * this.SMOOTHING;
        const now = Date.now();
        const isAbove = state.smoothedLevel > this.START_THRESHOLD;
        const isBelow = state.smoothedLevel < this.STOP_THRESHOLD;

        if (isAbove) {
          state.lastActive = now;
          if (!state.isSpeaking) {
            if (!this.isParticipantUnmuted(participantId)) {
              this.speakingState.set(participantId, state);
              return;
            }
            state.isSpeaking = true;
            state.speakingStartedAt = now;
            if (cardElement) cardElement.style.border = '2px solid #00ff00';
            useMeetingStore.getState().onParticipantSpeakingStarted(participantId);
            this.trackParticipantActivity(participantId, 'speaking', {
              audioLevel: state.smoothedLevel,
              duration: 0,
            });
          }
        } else if (isBelow && now - state.lastActive > this.DECAY_TIME && state.isSpeaking) {
          this.handleSpeakingStopped(participantId, cardElement, state);
        }

        this.speakingState.set(participantId, state);
      });

    this.lastPeriodicSortAt = Date.now();
    this.cleanupInterval = setInterval(() => {
      const now = Date.now();
      for (const [participantId, state] of this.speakingState.entries()) {
        if (state.isSpeaking && now - state.lastActive > this.DECAY_TIME) {
          const cardElement = document.getElementById(`participant-card-${participantId}`);
          this.handleSpeakingStopped(participantId, cardElement, state);
        }
      }

      if (now - this.lastPeriodicSortAt >= SPEAKING_SORT_INTERVAL_MS) {
        this.lastPeriodicSortAt = now;
        this.flushSpeakingDurations();
        useMeetingStore.getState().runParticipantPeriodicSort();
      }
    }, 500);
  }

  private isParticipantUnmuted(participantId: string): boolean {
    const storeParticipant = useMeetingStore.getState().jitsiParticipants[participantId];
    const storeTrack = storeParticipant?.audioTrack?.track;
    if (storeTrack && typeof storeTrack.isMuted === 'function') {
      return !storeTrack.isMuted();
    }

    const participant = this.getParticipantById(participantId);
    const audioTrack = participant?.tracks?.find((track) => track.type === MeetingTrackType.AUDIO);
    if (!audioTrack) return false;

    return !audioTrack.isMuted;
  }

  private handleSpeakingStopped(
    participantId: string,
    cardElement: HTMLElement | null,
    state: SpeakerAudioEvent
  ): void {
    state.isSpeaking = false;
    if (cardElement) cardElement.style.border = '0px';

    if (state.speakingStartedAt !== null) {
      const duration = Date.now() - state.speakingStartedAt;
      useMeetingStore.getState().addParticipantSpeakingTime(participantId, duration);
      state.speakingStartedAt = null;
    }

    useMeetingStore.getState().onParticipantSpeakingStopped(participantId);
  }

  private flushSpeakingDurations(): void {
    const now = Date.now();
    for (const [participantId, state] of this.speakingState.entries()) {
      if (!state.isSpeaking || state.speakingStartedAt === null) continue;
      const duration = now - state.speakingStartedAt;
      useMeetingStore.getState().addParticipantSpeakingTime(participantId, duration);
      state.speakingStartedAt = now;
    }
  }

  /**
   * Clean up audio level handlers and intervals
   */
  private cleanupAudioLevelHandlers(): void {
    if (this.audioLevelSubscription) {
      this.audioLevelSubscription.unsubscribe();
      this.audioLevelSubscription = null;
    }
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.flushSpeakingDurations();
    this.speakingState.clear();
  }

  async toggleTrack(type: MeetingTrackType, mute: boolean, participantId?: string): Promise<void> {
    try {
      const isLocal = !participantId || participantId === this.getMyUserId();

      if (!isLocal) {
        this.muteParticipant(participantId!);
        return;
      }

      const targetParticipant = this.getLocalParticipant();
      if (!targetParticipant) {
        console.warn('[CAM-TOGGLE] no targetParticipant — returning');
        return;
      }

      const track = targetParticipant.tracks.find((t) => t.type === type);

      // Detect whether the existing video track's underlying hardware was stopped by a prior
      // soft-mute. Such a track is muted but its nativeTrack shell still exists; treat it the
      // same as "no track" so we replace it with a fresh getUserMedia stream on unmute.
      const videoTrackUnderlying = track?.nativeTrack?.getTrack?.();
      const isVideoTrackStopped =
        type === MeetingTrackType.VIDEO &&
        !!track?.nativeTrack &&
        videoTrackUnderlying?.readyState === 'ended';

      // The same shell can outlive its hardware for AUDIO: something else on the page can
      // stop the underlying MediaStreamTrack (a device handoff, a global "stop all media"
      // sweep) while Jitsi still holds the track object. unmute() on that shell resolves
      // happily and transmits nothing, so the mic button appears dead. Treat it as missing
      // and let the on-demand path below build a fresh one.
      const audioTrackUnderlying = track?.nativeTrack?.getTrack?.();
      const isAudioTrackStopped =
        type === MeetingTrackType.AUDIO &&
        !!track?.nativeTrack &&
        audioTrackUnderlying?.readyState === 'ended';

      // If unmuting VIDEO and no usable track exists (never created, fully disposed, or
      // hardware-stopped by a prior soft-mute), create a fresh camera track on-demand.
      // Audio tracks are always created at init, so this only applies to VIDEO.
      if (
        !mute &&
        (!track || !track.nativeTrack || isVideoTrackStopped) &&
        this.conference &&
        type === MeetingTrackType.VIDEO
      ) {
        // Snapshot the conference at entry so we can detect floor navigation that
        // happens while any of the awaits below are pending. If the conference object
        // changes, this call was meant for a different (now-left) room and must abort.
        const conferenceAtEntry = this.conference;

        if (this.cameraUnavailable) {
          throw new Error('Camera is unavailable — device could not be started');
        }

        // Dispose the stale stopped-track shell before replacing it so the conference
        // and localTracks array stay consistent.
        if (isVideoTrackStopped && track?.nativeTrack) {
          const staleNative = track.nativeTrack;
          if (this.conference) {
            try {
              await this.conference.removeTrack(staleNative);
            } catch (_) {}
          }
          try {
            await staleNative.dispose();
          } catch (_) {}
          this.localTracks = this.localTracks.filter((t) => t !== staleNative);
          this.localTrackService.removeTrack(staleNative);
        }

        // If initLocalTracks is still running (POLICY-EFFECT fired between conference.join()
        // and initLocalTracks adding the forceVideoOn track), wait for it to finish. Without
        // this, conference.getLocalTracks() is empty and the guard below misses the track,
        // causing a duplicate camera stream to be opened.
        if (this._initTracksPromise) {
          await this._initTracksPromise.catch(() => {});
        }

        // After any await, verify the conference hasn't changed (user navigated away while
        // we were waiting). If it has, abort — otherwise we'd open a camera stream on the
        // new floor's conference with a stale participant ID, leaving the light ON but the
        // tile showing OFF.
        if (!this.conference || this.conference !== conferenceAtEntry) return;

        // Guard: if the conference already has a live unmuted local video track (e.g.
        // created by initLocalTracks with forceVideoOn), adopt it instead of creating a
        // second one. Prevents "Cannot add second video track" and orphaned camera streams.
        // Note: when isVideoTrackStopped the stale track was removed above, so
        // getLocalTracks() won't return it — the guard naturally falls through to create fresh.
        {
          const confVideoTrack = this.conference
            ?.getLocalTracks?.()
            ?.find((t: any) => t.getType() === 'video' && !t.videoType?.includes('desktop'));
          if (confVideoTrack && !confVideoTrack.isMuted()) {
            if (!this.localTracks.includes(confVideoTrack)) {
              this.localTracks.push(confVideoTrack);
              this.localTrackService.addTracks([confVideoTrack]);
            }
            this.eventSubject.next(
              this.eventMapper.createTrackUnmuted(type, targetParticipant.id, confVideoTrack)
            );
            return;
          }
        }

        const [newTrack] = await JitsiMeetJS.createLocalTracks({ devices: ['video'] });

        // getUserMedia is async — check the conference again after it resolves. If the
        // user navigated away during the camera start, dispose the brand-new track
        // immediately so we never leave an orphaned stream behind.
        if (!this.conference || this.conference !== conferenceAtEntry) {
          try {
            await newTrack.dispose();
          } catch (_) {}
          return;
        }

        this.conference.setSenderVideoConstraint(JITSI_DEFAULT_RESOLUTION);
        try {
          await this.conference.addTrack(newTrack);
        } catch (addError) {
          // addTrack failed — dispose the just-created track immediately so we don't
          // leave an orphaned camera stream open (keeps OS indicator on and may be
          // auto-added to the next conference by Jitsi).
          try {
            await newTrack.dispose();
          } catch (_) {}
          throw addError;
        }
        this.localTracks.push(newTrack);
        this.localTrackService.addTracks([newTrack]);

        this.eventSubject.next(
          this.eventMapper.createTrackUnmuted(type, targetParticipant.id, newTrack)
        );

        void this.applyVirtualBackgroundToLocalVideo();

        // Persist mute state to localStorage
        const currentState = this.loadMuteStateFromStorage();
        currentState.isVideoMuted = false;
        this.saveMuteStateToStorage(currentState);
        return;
      }

      // Same on-demand creation for AUDIO. The comment above ("audio tracks are always
      // created at init") does not hold in every path: creation can fail, the track can be
      // disposed by a ghost purge on a room switch, or the join can end up publishing
      // nothing. Whenever that happened the mic button silently did nothing forever,
      // because the guard below just returned. Build a fresh mic track instead.
      if (
        !mute &&
        (!track || !track.nativeTrack || isAudioTrackStopped) &&
        this.conference &&
        type === MeetingTrackType.AUDIO
      ) {
        const conferenceAtEntry = this.conference;

        if (this._initTracksPromise) {
          await this._initTracksPromise.catch(() => {});
        }
        if (!this.conference || this.conference !== conferenceAtEntry) return;

        // Drop the dead shell first so localTracks and the conference stay consistent.
        if (isAudioTrackStopped && track?.nativeTrack) {
          const staleMic = track.nativeTrack;
          try {
            await this.conference.removeTrack(staleMic);
          } catch (_) {
            /* ignore */
          }
          try {
            await staleMic.dispose();
          } catch (_) {
            /* ignore */
          }
          this.localTracks = this.localTracks.filter((t) => t !== staleMic);
          this.localTrackService.removeTrack(staleMic);
        }

        // Adopt a live unmuted mic track the conference already has rather than opening a
        // second one (mirrors the video guard above).
        const confAudioTrack = this.conference
          ?.getLocalTracks?.()
          ?.find((t: any) => t.getType() === 'audio');
        if (confAudioTrack) {
          if (!this.localTracks.includes(confAudioTrack)) {
            this.localTracks.push(confAudioTrack);
            this.localTrackService.addTracks([confAudioTrack]);
          }
          if (confAudioTrack.isMuted()) await confAudioTrack.unmute();
          this.eventSubject.next(
            this.eventMapper.createTrackUnmuted(type, targetParticipant.id, confAudioTrack)
          );
          const adoptedState = this.loadMuteStateFromStorage();
          adoptedState.isAudioMuted = false;
          this.saveMuteStateToStorage(adoptedState);
          return;
        }

        const [newAudioTrack] = await JitsiMeetJS.createLocalTracks({ devices: ['audio'] });

        // createLocalTracks is async — the user may have navigated away meanwhile.
        if (!this.conference || this.conference !== conferenceAtEntry) {
          try {
            await newAudioTrack.dispose();
          } catch (_) {
            /* ignore */
          }
          return;
        }

        try {
          await this.conference.addTrack(newAudioTrack);
        } catch (addError) {
          try {
            await newAudioTrack.dispose();
          } catch (_) {
            /* ignore */
          }
          throw addError;
        }

        this.localTracks.push(newAudioTrack);
        this.localTrackService.addTracks([newAudioTrack]);
        this.eventSubject.next(
          this.eventMapper.createTrackUnmuted(type, targetParticipant.id, newAudioTrack)
        );

        const currentAudioState = this.loadMuteStateFromStorage();
        currentAudioState.isAudioMuted = false;
        this.saveMuteStateToStorage(currentAudioState);
        return;
      }

      if (!track || !track.nativeTrack) {
        console.warn('[CAM-TOGGLE] no track/nativeTrack — returning early (mute=' + mute + ')');
        return;
      }

      const nativeTrack = track.nativeTrack;
      // Guard: only persist mute state when we actually changed the track — skipping
      // no-op calls prevents a policy re-enforcement (camera already on) from writing
      // isVideoMuted=false to localStorage and causing the camera to auto-open when
      // the user joins their next (non-policy) conference.
      let stateActuallyChanged = false;
      if (mute && !nativeTrack.isMuted()) {
        if (type === MeetingTrackType.VIDEO) {
          // Soft-stop: release the OS camera hardware but keep the Jitsi track
          // shell alive in localTracks / localTrackService. Fully disposing here
          // creates a race window where policy effects see no track and recreate
          // it immediately (camera turns back on). By keeping the shell alive and
          // marking it muted, consumers find an existing muted track instead of
          // triggering on-demand camera re-creation. The shell is cleaned up the
          // next time the user re-enables the camera (isVideoTrackStopped path above).
          await nativeTrack.mute();
          // 2. Stop every live video MediaStreamTrack to release the OS camera light.
          //    Multiple sources may reference the same camera; deduplicate via a Set.
          //    IMPORTANT: capture effect streams BEFORE calling setEffect(undefined) —
          //    teardown sets nativeTrack._effect = undefined, losing the reference to
          //    effect._stream (which holds the raw camera MediaStream when VB is active).
          const cameraTracksToStop = new Set<MediaStreamTrack>();
          const addVideoTracks = (s: unknown) => {
            if (s instanceof MediaStream) {
              s.getVideoTracks().forEach((t) => cameraTracksToStop.add(t));
            }
          };
          try {
            // Effect streams — captured now, before setEffect(undefined) clears _effect
            const effect = (nativeTrack as any)?._effect;
            if (effect) {
              for (const k of ['_stream', 'stream', '_inputStream', 'inputStream']) {
                addVideoTracks((effect as any)[k]);
              }
            }
          } catch (_) {}
          try {
            addVideoTracks(nativeTrack.getOriginalStream?.());
          } catch (_) {}
          try {
            const t = nativeTrack.getTrack?.();
            if (t instanceof MediaStreamTrack && t.kind === 'video') cameraTracksToStop.add(t);
          } catch (_) {}
          try {
            const any = nativeTrack as any;
            for (const k of ['stream', '_stream', '_originalStream', 'originalStream']) {
              addVideoTracks(any[k]);
            }
          } catch (_) {}

          // 1. Clear VB effect AFTER capturing its streams above.
          //    setEffect(undefined) sets nativeTrack._effect = undefined synchronously,
          //    so it must come after the effect-stream capture, not before.
          try {
            await nativeTrack.setEffect?.(undefined);
          } catch (e) {
            console.warn('[CAM-TOGGLE] setEffect(undefined) threw:', e);
          }

          // Re-probe post-teardown: lib-jitsi-meet restores _stream to the raw camera
          // stream after VB teardown, so there may be additional tracks to add.
          try {
            addVideoTracks(nativeTrack.getOriginalStream?.());
          } catch (_) {}
          try {
            const t = nativeTrack.getTrack?.();
            if (t instanceof MediaStreamTrack && t.kind === 'video') cameraTracksToStop.add(t);
          } catch (_) {}
          try {
            const any = nativeTrack as any;
            for (const k of ['stream', '_stream', '_originalStream', 'originalStream']) {
              addVideoTracks(any[k]);
            }
          } catch (_) {}

          cameraTracksToStop.forEach((t) => {
            if (t.readyState === 'live') t.stop();
          });
        } else {
          await nativeTrack.mute();
        }
        this.eventSubject.next(
          this.eventMapper.createTrackMuted(type, targetParticipant.id, nativeTrack)
        );
        stateActuallyChanged = true;
      } else if (!mute && nativeTrack.isMuted()) {
        await nativeTrack.unmute();
        this.eventSubject.next(
          this.eventMapper.createTrackUnmuted(type, targetParticipant.id, nativeTrack)
        );
        stateActuallyChanged = true;
      }

      // Reconcile NS after a local audio mute/unmute: lazy apply turns it on once the mic
      // is live (e.g. unmuting after joining muted), and is a no-op while still muted.
      if (isLocal && type === MeetingTrackType.AUDIO) {
        await this.applyNoiseSuppression();
      }

      // Persist mute state to localStorage — but ONLY when we actually changed something.
      if (stateActuallyChanged) {
        const currentState = this.loadMuteStateFromStorage();
        if (type === MeetingTrackType.VIDEO) {
          currentState.isVideoMuted = mute;
        } else if (type === MeetingTrackType.AUDIO) {
          currentState.isAudioMuted = mute;
        }
        this.saveMuteStateToStorage(currentState);
      }
    } catch (error) {
      Logger.warn('[JitsiMeetingService] toggleTrack failed:', error);
      this.eventSubject.next(this.eventMapper.createTrackError(type, error));
    }
  }

  private getTrackTypeFromJitsiTrack(track: any): MeetingTrackType {
    if (!track) return MeetingTrackType.VIDEO;
    const type = track.getType();
    if (type === 'audio') return MeetingTrackType.AUDIO;
    return type === 'video' && track.videoType === 'desktop'
      ? MeetingTrackType.SCREEN
      : MeetingTrackType.VIDEO;
  }

  async muteParticipant(participantId: string): Promise<void> {
    if (!this.conference) throw new Error('Conference not joined.');
    try {
      await this.conference.muteParticipant(participantId);
      this.eventSubject.next(this.eventMapper.createParticipantMuted(participantId));
    } catch (error) {
      this.eventSubject.next(this.eventMapper.createTrackError(MeetingTrackType.AUDIO, error));
    }
  }

  async kickParticipant(
    participantId: string,
    reason: string = 'You have been kicked.'
  ): Promise<void> {
    if (!this.conference) throw new Error('Conference not joined.');
    try {
      await this.conference.kickParticipant(participantId, reason);
      this.eventSubject.next(this.eventMapper.createParticipantKicked(participantId, reason));
    } catch (error) {
      this.eventSubject.next(this.eventMapper.createLocalTracksError(error));
    }
  }

  /**
   * NGE-681 (Auditorium): everyone arrives with the microphone muted. In a room that can
   * hold hundreds, an attendee joining live is disruptive, so the start-muted policy
   * applies to the whole conference — but it is only a starting state, and anyone may
   * turn their own microphone back on. Video is left alone: guests join with the camera
   * on so they are identifiable on the stage.
   *
   * This used to also switch on AV moderation, which held non-moderators muted until
   * a moderator approved them individually. That is gone: a guest has a microphone and
   * a camera of their own, and a moderator who wants the room quiet mutes it from the
   * moderator panel instead.
   *
   * Must be called by a moderator (no-op / rejected by Jicofo otherwise). Safe to call
   * when unsupported.
   */
  async enforceStartMuted(): Promise<void> {
    if (!this.conference) return;
    try {
      this.conference.setStartMutedPolicy?.({ audio: true, video: false });
      Logger.debug('[JitsiMeetingService] Start-muted policy applied');
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to apply the start-muted policy:', error);
    }
  }

  /** NGE-681: lift audience moderation (moderator only). */
  async disableAudienceModeration(): Promise<void> {
    if (!this.conference) return;
    try {
      this.conference.disableAVModeration?.('audio');
      this.conference.disableAVModeration?.('video');
      Logger.debug('[JitsiMeetingService] Audience moderation disabled');
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to disable audience moderation:', error);
    }
  }

  /**
   * NGE-681: approve a specific participant to publish (promote audience → speaker
   * at the media layer). Moderator only.
   */
  async approveParticipantMedia(participantId: string): Promise<void> {
    if (!this.conference) return;
    try {
      this.conference.avModerationApprove?.('audio', participantId);
      this.conference.avModerationApprove?.('video', participantId);
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to approve participant media:', error);
    }
  }

  /**
   * NGE-681: ask the bridge for the incoming streams an auditorium stage needs.
   *
   * Only tunes what is *received* — the receiver video constraint and lastN — so it
   * applies to every attendee, not just one who cannot publish. Without it the bridge
   * forwards too little to fill the stage, including a presenter's screen share.
   */
  async configureReceiveOnly(): Promise<void> {
    if (!this.conference) return;
    try {
      this.conference.setReceiverVideoConstraint?.(JITSI_DEFAULT_RESOLUTION);
      // Ensure the bridge forwards enough streams (speakers + screen share).
      this.conference.setLastN?.(20);
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to configure receive-only video:', error);
    }
  }

  /**
   * NGE-681: let a viewer pick the incoming video quality for their device/network.
   * `maxHeight` is the max received resolution (e.g. 1080/720/360/180); the bridge
   * caps forwarded video accordingly.
   */
  async setReceiverVideoQuality(maxHeight: number): Promise<void> {
    if (!this.conference) return;
    try {
      this.conference.setReceiverVideoConstraint?.(maxHeight);
      this.currentResolution = maxHeight;
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to set receiver video quality:', error);
    }
  }

  async sendMessageToParticipant(text: string, participantId: string): Promise<void> {
    if (!this.conference) {
      Logger.error('Conference not joined.');
      return;
    }
    return this.conference.sendMessage(text, participantId);
  }

  private async getLocalVideoTrack(): Promise<any | null> {
    const local = this.conference?.getLocalTracks?.() ?? this.localTrackService.tracks;
    return local?.find((t: any) => t.getType?.() === 'video') ?? null;
  }

  private async applyVirtualBackgroundToLocalVideo(): Promise<void> {
    const track = await this.getLocalVideoTrack();
    if (!track) return;

    try {
      await track.setEffect(undefined);
      if (this.vbOptions.backgroundEffectEnabled && this.effectFactory) {
        const effect = await this.effectFactory(this.vbOptions);
        await track.setEffect(effect);
      } else {
        await track.setEffect(undefined);
      }
    } catch (err) {
      this.vbOptions.backgroundEffectEnabled = false;
      Logger.error('[JitsiMeetingService] apply VB error:', err);
    }
  }

  setVirtualBackgroundOptions(patch: Partial<VirtualBackgroundOptions>, applyNow = false): void {
    this.vbOptions = { ...this.vbOptions, ...patch };
    if (applyNow) void this.applyVirtualBackgroundToLocalVideo();
  }

  async toggleVirtualBackground(
    enabled: boolean,
    extra?: Partial<VirtualBackgroundOptions>
  ): Promise<void> {
    this.vbOptions = { ...this.vbOptions, ...extra, backgroundEffectEnabled: enabled };
    this.saveVbStateToStorage(this.vbOptions);
    await this.applyVirtualBackgroundToLocalVideo();
  }

  async applyBlurBackground(blur: BlurPreset = 'blur', muted = false): Promise<void> {
    if (muted || blur === 'none') {
      await this.toggleVirtualBackground(false, {
        backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
        blurValue: 0,
        selectedThumbnail: 'none',
      });
      return;
    }

    await this.toggleVirtualBackground(true, {
      backgroundType: VIRTUAL_BACKGROUND_TYPE.BLUR,
      blurValue: blur === 'blur' ? 25 : 8,
      selectedThumbnail: blur,
    });
  }

  async enableImageBackground(url: string) {
    await this.toggleVirtualBackground(true, {
      backgroundType: VIRTUAL_BACKGROUND_TYPE.IMAGE,
      virtualSource: url,
      blurValue: 0,
      selectedThumbnail: url,
    });
  }

  async disableBackground() {
    await this.toggleVirtualBackground(false);
  }

  async setBlurPreset(preset: BlurPreset, muted = false) {
    await this.applyBlurBackground(preset, muted);
  }

  // ─── Noise Suppression ────────────────────────────────────────────────────

  private static loadNsStateFromStorage(): boolean {
    try {
      const raw = localStorage.getItem(JitsiMeetingService.NS_STATE_KEY);
      return raw ? (JSON.parse(raw) as { enabled: boolean }).enabled : false;
    } catch {
      return false;
    }
  }

  private saveNsStateToStorage(enabled: boolean): void {
    try {
      localStorage.setItem(JitsiMeetingService.NS_STATE_KEY, JSON.stringify({ enabled }));
    } catch {
      // ignore storage errors
    }
  }

  private getLocalAudioTrack(): any | null {
    const tracks = this.conference?.getLocalTracks?.() ?? this.localTrackService.tracks;
    return tracks?.find((t: any) => t.getType?.() === 'audio') ?? null;
  }

  /**
   * Reconciles the noise-suppression effect with the current state.
   *
   * Lazy by design: the effect runs ONLY while NS is enabled AND the mic is unmuted.
   * While muted nothing is transmitted, so we never set the effect up on a muted track —
   * that setup is what briefly leaked audio. Once applied (while unmuted) the effect is
   * left in place across mute/unmute (lib-jitsi-meet's mute gates its output) and is torn
   * down only when NS is disabled. Safe to call any time; it only acts on a state change.
   */
  private async applyNoiseSuppression(): Promise<void> {
    if (this._nsApplying) return;
    this._nsApplying = true;

    try {
      const track = this.getLocalAudioTrack();
      if (!track) return;

      if (this.nsEnabled) {
        // Apply only when not already active and the mic is live — applying to a muted
        // track is what caused the audio leak during the effect's startup.
        if (!this.nsEffect && !track.isMuted()) {
          const effect = new NoiseSuppressionEffect();
          await effect.initialize();
          await track.setEffect(effect);
          this.nsEffect = effect;
        }
      } else if (this.nsEffect) {
        await track.setEffect(undefined);
        this.nsEffect = null;
      }
    } catch (err) {
      this.nsEnabled = false;
      this.saveNsStateToStorage(false);
      this.nsEffect = null;
      Logger.error('[JitsiMeetingService] Noise suppression error:', err);
    } finally {
      this._nsApplying = false;
    }
  }

  /**
   * Loads the user's stored Noise Suppression preference and reconciles the effect with it.
   *
   * localStorage only seeds the initial value, so it is lost on a cache clear or on a different
   * browser. The server-side user preference is the source of truth — this pulls it in and
   * re-applies the effect if the stored state differs from what is currently running.
   *
   * @returns The resulting enabled state (unchanged when the user has never set the preference).
   */
  async hydrateNoiseSuppressionPreference(): Promise<boolean> {
    const stored = await fetchNoiseSuppressionPreference();

    // null = never set by this user; keep whatever we already have rather than forcing OFF.
    if (stored === null || stored === this.nsEnabled) {
      return this.nsEnabled;
    }

    this.nsEnabled = stored;
    await this.applyNoiseSuppression();
    this.saveNsStateToStorage(this.nsEnabled);
    return this.nsEnabled;
  }

  /**
   * Toggles noise suppression and returns the ACTUAL resulting state.
   * applyNoiseSuppression() catches its own errors and resets nsEnabled to false if applying
   * the effect fails, so we persist and return the real state here — letting the UI reflect
   * reality (e.g. revert the switch) instead of assuming the toggle succeeded.
   */
  async toggleNoiseSuppression(enabled: boolean): Promise<boolean> {
    this.nsEnabled = enabled;
    await this.applyNoiseSuppression();
    this.saveNsStateToStorage(this.nsEnabled);
    // Persisted server-side so the choice survives a cache clear, a new device and a redeploy.
    // Persisting the ACTUAL state matters: if applying the effect failed we must not store a
    // preference the user never got.
    await persistNoiseSuppressionPreference(this.nsEnabled);
    return this.nsEnabled;
  }

  getNoiseSuppressorEnabled(): boolean {
    return this.nsEnabled;
  }

  // ─── Breakout Rooms Related code ─────────────────────────────────────────

  async createBreakoutRoom(name: string): Promise<string> {
    if (!this.conference) throw new Error('Conference not joined.');
    this.conference.getBreakoutRooms().createBreakoutRoom(name);
    //get rooms wait for room to be created then return the room id
    return new Promise((resolve) => {
      const checkRoomCreated = () => {
        if (this.conference.getBreakoutRooms()._rooms) {
          const rooms = this.conference.getBreakoutRooms()._rooms;

          const room: any = Object.values(rooms).find((r: any) => r.name === name);

          if (room) {
            resolve(room.jid);
          } else {
            setTimeout(checkRoomCreated, 500);
          }
        } else {
          setTimeout(checkRoomCreated, 500);
        }
      };
      checkRoomCreated();
    });
  }

  async removeBreakoutRoom(breakoutRoomJid: string): Promise<any> {
    if (!this.conference) throw new Error('Conference not joined.');
    return this.conference.getBreakoutRooms().removeBreakoutRoom(breakoutRoomJid);
  }

  async sendParticipantToBreakoutRoom(participantJid: string, roomJid: string): Promise<any> {
    if (!this.conference) throw new Error('Conference not joined.');
    return this.conference.getBreakoutRooms().sendParticipantToRoom(participantJid, roomJid);
  }

  async sendParticipantToMainRoom(participantJid: string): Promise<any> {
    if (!this.conference) throw new Error('Conference not joined.');
    let mainRoomJid;
    const roomObject = this.conference.getBreakoutRooms();

    if (!roomObject._isBreakoutRoom) {
      mainRoomJid = roomObject.room?.roomjid;
      if (!mainRoomJid) {
        throw new Error('Main room JID is undefined.');
      }
    } else {
      mainRoomJid = this.conference.getBreakoutRooms().getMainRoomJid();
    }

    return this.conference.getBreakoutRooms().sendParticipantToRoom(participantJid, mainRoomJid);
  }

  getBreakoutRooms(): any {
    return this.conference.getBreakoutRooms();
  }

  async setDisplayName(name: string): Promise<void> {
    if (!this.conference) throw new Error('Conference not joined.');
    this.conference.setDisplayName(name);
  }

  attachTrackToElement(trackId: string, container: HTMLElement): void {
    const jitsiTrack = this.findTrackById(trackId);
    if (!jitsiTrack || !container) return;

    jitsiTrack.attach(container);
    this.eventSubject.next(this.eventMapper.createTrackAttached(trackId, container));
  }

  detachTrackFromElement(trackId: string, container: HTMLElement): void {
    const jitsiTrack = this.findTrackById(trackId);
    if (!jitsiTrack || !container) return;

    jitsiTrack.detach(container);
    this.eventSubject.next(this.eventMapper.createTrackDetached(trackId, container));
  }

  private findTrackById(trackId: string): any {
    // Check local tracks first
    const localTrack = this.localTrackService.tracks.find((track) => track.getId() === trackId);
    if (localTrack) return localTrack;

    // Then check remote tracks
    if (this.conference) {
      for (const participant of this.conference.getParticipants()) {
        const track = participant.getTracks().find((t: any) => t.getId() === trackId);
        if (track) return track;
      }
    }

    return null;
  }

  private static MUTE_STATE_KEY = 'meeting-mute-state';
  private static VB_STATE_KEY = 'meeting-vb-state';

  private saveVbStateToStorage(state: VirtualBackgroundOptions): void {
    try {
      localStorage.setItem(JitsiMeetingService.VB_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('[JitsiMeetingService] Error saving VB state to localStorage:', error);
    }
  }

  private loadVbStateFromStorage(): VirtualBackgroundOptions {
    try {
      const saved = localStorage.getItem(JitsiMeetingService.VB_STATE_KEY);
      return saved
        ? JSON.parse(saved)
        : {
            backgroundEffectEnabled: false,
            backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
            blurValue: 0,
            selectedThumbnail: 'none',
          };
    } catch (error) {
      console.warn('[JitsiMeetingService] Error loading VB state from localStorage:', error);
      return {
        backgroundEffectEnabled: false,
        backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
        blurValue: 0,
        selectedThumbnail: 'none',
      };
    }
  }

  /**
   * Seed the mic/camera state the NEXT join starts from, without needing a live service
   * instance. The guest forms run before initializeJitsiMeetingService(), so the singleton
   * is still null there — hence static.
   */
  static setPersistedMuteState(state: { isVideoMuted: boolean; isAudioMuted: boolean }): void {
    try {
      localStorage.setItem(JitsiMeetingService.MUTE_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('[JitsiMeetingService] Error saving mute state to localStorage:', error);
    }
  }

  /** The persisted mute state the next join starts from (both muted when nothing is saved). */
  static getPersistedMuteState(): { isVideoMuted: boolean; isAudioMuted: boolean } {
    try {
      const saved = localStorage.getItem(JitsiMeetingService.MUTE_STATE_KEY);
      return saved ? JSON.parse(saved) : { isVideoMuted: true, isAudioMuted: true };
    } catch {
      return { isVideoMuted: true, isAudioMuted: true };
    }
  }

  private saveMuteStateToStorage(state: { isVideoMuted: boolean; isAudioMuted: boolean }): void {
    JitsiMeetingService.setPersistedMuteState(state);
  }

  private loadMuteStateFromStorage(): { isVideoMuted: boolean; isAudioMuted: boolean } {
    try {
      const saved = localStorage.getItem(JitsiMeetingService.MUTE_STATE_KEY);
      return saved ? JSON.parse(saved) : { isVideoMuted: true, isAudioMuted: true };
    } catch (error) {
      console.warn('[JitsiMeetingService] Error loading mute state from localStorage:', error);
      return { isVideoMuted: true, isAudioMuted: true };
    }
  }

  getLocalMuteState(): { isVideoMuted: boolean; isAudioMuted: boolean } {
    const savedState = this.loadMuteStateFromStorage();
    const videoTrack = this.localTracks.find((t: any) => t.getType() === 'video');
    const audioTrack = this.localTracks.find((t: any) => t.getType() === 'audio');

    return {
      // Video: If track doesn't exist, it was skipped because it was muted - use localStorage
      // Audio: Track should always exist (even if muted) - use actual track state
      isVideoMuted: videoTrack ? videoTrack.isMuted() : savedState.isVideoMuted,
      isAudioMuted: audioTrack ? audioTrack.isMuted() : savedState.isAudioMuted,
    };
  }

  async restoreMuteState(muteState?: {
    isVideoMuted: boolean;
    isAudioMuted: boolean;
  }): Promise<void> {
    const state = muteState ?? this.loadMuteStateFromStorage();
    const myUserId = this.getMyUserId();
    for (const track of this.localTracks) {
      try {
        if (track.getType() === 'video' && state.isVideoMuted && !track.isMuted()) {
          await track.mute();
          this.eventSubject.next(
            this.eventMapper.createTrackMuted(MeetingTrackType.VIDEO, myUserId, track)
          );
        }
        if (track.getType() === 'audio' && state.isAudioMuted && !track.isMuted()) {
          await track.mute();
          this.eventSubject.next(
            this.eventMapper.createTrackMuted(MeetingTrackType.AUDIO, myUserId, track)
          );
        }
      } catch (error) {
        console.warn('[JitsiMeetingService] Error restoring mute state:', error);
      }
    }
  }

  getLocalParticipant(): MeetingParticipant | null {
    if (!this.conference) return null;

    const id = this.conference.myUserId();
    const displayName = this.conference.getName();

    const tracks: MeetingTrack[] = this.localTrackService.tracks.map((track) => ({
      id: track.getId(),
      type: this.getTrackTypeFromJitsiTrack(track),
      participantId: id,
      isMuted: track.isMuted(),
      streamId: track.getStreamId(),
      stream: track.getOriginalStream(),
      nativeTrack: track,
    }));

    const wpUser = useWorkplaceStore.getState().user;

    return {
      id,
      displayName,
      isLocalParticipant: true,
      tracks,
      userPid: useWorkplaceStore.getState().user.pid,
      avatarUrl: useWorkplaceStore.getState().user.avatar_url,
      avatarThumbnailUrl: wpUser.avatar_thumbnail_url ?? undefined,
    };
  }

  getParticipants(): MeetingParticipant[] {
    if (!this.conference) return [];

    const localParticipant = this.getLocalParticipant();
    const remoteParticipants = this.conference.getParticipants().map((participant: any) => {
      return {
        id: participant.getId(),
        userPid: participant._identity?.user?.id,
        avatar: participant._identity?.user?.avatar,
        avatar_thumbnail: participant._identity?.user?.avatar_thumbnail,
        displayName: participant._identity?.user?.name ?? participant.getDisplayName(),
        isLocalParticipant: false,
        tracks: participant.getTracks().map((track: any) => ({
          id: track.getId(),
          type: this.getTrackTypeFromJitsiTrack(track),
          participantId: participant.getId(),
          isMuted: track.isMuted(),
          streamId: track.getStreamId(),
          stream: track.getOriginalStream(),
          nativeTrack: track,
        })),
      };
    });

    return localParticipant ? [localParticipant, ...remoteParticipants] : remoteParticipants;
  }

  getParticipantById(participantId: string): MeetingParticipant | null {
    if (!this.conference) return null;

    const localParticipant = this.getLocalParticipant();
    if (localParticipant && localParticipant.id === participantId) {
      return localParticipant;
    }

    const remoteParticipant = this.conference.getParticipantById(participantId);
    if (!remoteParticipant) return null;

    return {
      id: remoteParticipant.getId(),
      displayName: remoteParticipant._identity?.user?.name ?? remoteParticipant.getDisplayName(),
      isLocalParticipant: false,
      userPid: remoteParticipant._identity?.user?.id,
      avatarUrl: remoteParticipant._identity?.user?.avatar,
      avatarThumbnailUrl: remoteParticipant._identity?.user?.avatar_thumbnail,
      tracks: remoteParticipant.getTracks().map((track: any) => ({
        id: track.getId(),
        type: this.getTrackTypeFromJitsiTrack(track),
        participantId: remoteParticipant.getId(),
        isMuted: track.isMuted(),
        streamId: track.getStreamId(),
        stream: track.getOriginalStream(),
        nativeTrack: track,
      })),
    };
  }

  getParticipantJidById(id: string) {
    return this.conference.getParticipantById(id)?.getConnectionJid();
  }

  /** Authoritative remote-participant IDs currently in the conference, per Jitsi itself
   * (not our event-derived store). Used to reconcile jitsiParticipants and self-heal
   * duplicate/stale/missing tiles when a USER_JOINED/USER_LEFT event was missed or
   * arrived out of order (e.g. during a breakout room switch). */
  getParticipantIds(): string[] {
    if (!this.conference) return [];
    return this.conference.getParticipants().map((p: any) => p.getId());
  }

  getAudioInputDevice(): string {
    try {
      const audioTrack = this.getLocalAudioTrack();
      if (audioTrack) {
        // Prefer getDeviceId(): stable even under NS, where the underlying track is the
        // effect output. getTrack().getSettings() returns '' then, triggering a spurious
        // device switch every time the manager opens.
        const realId = audioTrack.getDeviceId?.();
        if (realId) return realId;

        const track = audioTrack.getTrack?.();
        if (track?.getSettings) {
          return track.getSettings().deviceId || '';
        }
      }

      return '';
    } catch (error) {
      Logger.error('Error getting audio input device:', error);
      return '';
    }
  }

  getAudioOutputDevice(): string {
    try {
      if (typeof HTMLMediaElement.prototype.setSinkId !== 'function') {
        Logger.warn('Audio output selection not supported in this browser');
        return '';
      }

      if (this.conference && JitsiMeetJS.mediaDevices.getAudioOutputDevice) {
        return JitsiMeetJS.mediaDevices.getAudioOutputDevice();
      }

      return '';
    } catch (error) {
      Logger.error('Error getting audio output device:', error);
      return '';
    }
  }

  getVideoInputDevice(): string {
    try {
      // Get the active track if available
      const localTracks = this.conference?.getLocalTracks() || [];
      const videoTrack = localTracks.find((track: any) => track.getType() === 'video');

      if (videoTrack) {
        const track = videoTrack.getTrack();
        if (track && track.getSettings) {
          const settings = track.getSettings();
          return settings.deviceId || '';
        }
      }

      return '';
    } catch (error) {
      Logger.error('Error getting video input device:', error);
      return '';
    }
  }

  private loadSavedDevices(): { audioInput?: string; audioOutput?: string; videoInput?: string } {
    try {
      const saved = localStorage.getItem('meeting-device-settings');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      Logger.error('Failed to load saved devices:', error);
      return {};
    }
  }

  private async applySavedDevicePreferences(): Promise<void> {
    try {
      const savedDevices = this.loadSavedDevices();
      const devices = await navigator.mediaDevices.enumerateDevices();

      // a virtual device is never "available"
      const isDeviceAvailable = (deviceId: string, kind: string) => {
        return devices.some(
          (d) => d.deviceId === deviceId && d.kind === kind && !isVirtualAudioDevice(d)
        );
      };

      const currentAudioInput = this.getAudioInputDevice();
      const currentAudioOutput = this.getAudioOutputDevice();
      const currentVideoInput = this.getVideoInputDevice();

      if (
        savedDevices.audioInput &&
        isDeviceAvailable(savedDevices.audioInput, 'audioinput') &&
        savedDevices.audioInput !== currentAudioInput
      ) {
        await this.setAudioInputDevice(savedDevices.audioInput);
      }

      if (
        savedDevices.audioOutput &&
        isDeviceAvailable(savedDevices.audioOutput, 'audiooutput') &&
        savedDevices.audioOutput !== currentAudioOutput
      ) {
        await this.setAudioOutputDevice(savedDevices.audioOutput);
      }

      if (
        savedDevices.videoInput &&
        isDeviceAvailable(savedDevices.videoInput, 'videoinput') &&
        savedDevices.videoInput !== currentVideoInput
      ) {
        await this.setVideoInputDevice(savedDevices.videoInput);
      }

      // OS default can be virtual even with no saved preference — move off it
      const firstRealDeviceId = (kind: 'audioinput' | 'audiooutput') =>
        devices.find((d) => d.kind === kind && !isVirtualAudioDevice(d))?.deviceId;

      const activeInput = devices.find(
        (d) => d.kind === 'audioinput' && d.deviceId === this.getAudioInputDevice()
      );
      if (activeInput && isVirtualAudioDevice(activeInput)) {
        const realInput = firstRealDeviceId('audioinput');
        if (realInput) {
          Logger.info(
            '[JitsiMeetingService] Active microphone is virtual, switching to a real device'
          );
          await this.setAudioInputDevice(realInput);
        }
      }

      const activeOutput = devices.find(
        (d) => d.kind === 'audiooutput' && d.deviceId === this.getAudioOutputDevice()
      );
      if (activeOutput && isVirtualAudioDevice(activeOutput)) {
        const realOutput = firstRealDeviceId('audiooutput');
        if (realOutput) {
          Logger.info(
            '[JitsiMeetingService] Active speaker is virtual, switching to a real device'
          );
          await this.setAudioOutputDevice(realOutput);
        }
      }
    } catch (error) {
      Logger.error('Failed to apply saved device preferences:', error);
    }
  }

  /**
   * Upgrades video quality from fast-start constraints to optimal constraints
   * Uses applyConstraints on the existing track to avoid track replacement
   */
  public async upgradeVideoQuality(constraints: any, resolution: number): Promise<void> {
    try {
      const videoTrack = await this.getLocalVideoTrack();
      if (!videoTrack) return;

      const nativeTrack = videoTrack.getTrack();
      if (!nativeTrack || typeof nativeTrack.applyConstraints !== 'function') {
        Logger.warn('[JitsiMeetingService] applyConstraints not supported');
        return;
      }

      await nativeTrack.applyConstraints(constraints);

      if (this.conference) {
        this.conference.setSenderVideoConstraint(resolution);
        this.currentResolution = resolution;
      }

      Logger.debug('[JitsiMeetingService] Video quality upgraded to optimal settings');
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Failed to upgrade video quality:', error);
    }
  }

  // ─── Device change handling ────────────────────────────────────────────────

  /**
   * Returns the deviceId of the currently active local track for the given type,
   * or null if unavailable (e.g. no conference, no track, or browser doesn't expose it).
   */
  // @ts-expect-error method unused externally but kept for future device-switching support
  private getActiveDeviceId(type: 'audio' | 'video'): string | null {
    const tracks = this.conference?.getLocalTracks() ?? [];
    const track = tracks.find((t: any) => t.getType() === type);
    return track?.track?.getSettings?.()?.deviceId ?? null;
  }

  /**
   * Registers a debounced `devicechange` listener that falls back to the OS
   * default device if the currently active audio or video input disappears
   * (e.g. bluetooth headset powered off mid-call).
   *
   * The listener only acts on removal of the active device — it does NOT
   * automatically switch to a newly connected device the user hasn't chosen.
   */
  // 'default' can resolve to the virtual OS default — prefer the first real device instead.
  private async pickFallbackDeviceId(kind: 'audioinput' | 'audiooutput'): Promise<string> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const realDevice = devices.find((d) => d.kind === kind && !isVirtualAudioDevice(d));
      if (realDevice?.deviceId) return realDevice.deviceId;
    } catch (error) {
      Logger.warn('[JitsiMeetingService] Could not enumerate devices for fallback:', error);
    }
    // No real device found — let the browser default decide rather than nothing.
    return 'default';
  }

  private setupDeviceChangeListener(): void {
    this.teardownDeviceChangeListener();

    const handler = debounce(async () => {
      if (!this.conference) return;
      try {
        const localTracks = this.conference.getLocalTracks() ?? [];
        const activeAudioTrack = localTracks.find((t: any) => t.getType() === 'audio');
        const activeVideoTrack = localTracks.find((t: any) => t.getType() === 'video');

        // Primary signal: when a BT device is powered off Chrome immediately marks
        // the underlying MediaStreamTrack as 'ended' and getSettings() returns {},
        // so the deviceId-based check below would silently no-op. Check readyState first.
        const audioEnded = activeAudioTrack?.track?.readyState === 'ended';
        const videoEnded = activeVideoTrack?.track?.readyState === 'ended';

        if (audioEnded) {
          Logger.info(
            '[JitsiMeetingService] Active audio track ended (device removed), falling back to a real device'
          );
          await this.setAudioInputDevice(await this.pickFallbackDeviceId('audioinput'));
        }

        if (videoEnded) {
          Logger.info(
            '[JitsiMeetingService] Active video track ended (device removed), falling back to default'
          );
          await this.setVideoInputDevice('default');
        }

        // Belt-and-suspenders: also catch the case where the track is not yet
        // 'ended' but the device has already disappeared from the enumerated list.
        if (!audioEnded || !videoEnded) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioIds = new Set(
            devices.filter((d) => d.kind === 'audioinput').map((d) => d.deviceId)
          );
          const videoIds = new Set(
            devices.filter((d) => d.kind === 'videoinput').map((d) => d.deviceId)
          );

          if (!audioEnded) {
            const activeAudioId = activeAudioTrack?.track?.getSettings?.()?.deviceId;
            if (activeAudioId && !audioIds.has(activeAudioId)) {
              Logger.info(
                '[JitsiMeetingService] Active audio device removed from list, falling back to a real device'
              );
              await this.setAudioInputDevice(await this.pickFallbackDeviceId('audioinput'));
            }
          }

          if (!videoEnded) {
            const activeVideoId = activeVideoTrack?.track?.getSettings?.()?.deviceId;
            if (activeVideoId && !videoIds.has(activeVideoId)) {
              Logger.info(
                '[JitsiMeetingService] Active video device removed from list, falling back to default'
              );
              await this.setVideoInputDevice('default');
            }
          }
        }
      } catch (error) {
        Logger.warn('[JitsiMeetingService] Device change handling failed:', error);
      }
    }, 500);

    this.deviceChangeHandler = handler;
    navigator.mediaDevices.addEventListener('devicechange', handler);
  }

  private teardownDeviceChangeListener(): void {
    if (this.deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
  }

  // ───────────────────────────────────────────────────────────────────────────

  private async replaceOrAddTrack(type: 'audio' | 'video', deviceId: string): Promise<void> {
    if (!this.conference) {
      throw new Error('Cannot replace track: Conference not joined.');
    }

    const deviceOption =
      type === 'audio' ? { micDeviceId: deviceId } : { cameraDeviceId: deviceId };

    const localTracks = this.conference.getLocalTracks() || [];
    const existingTrack = localTracks.find((track: any) => track.getType() === type);

    const wasMuted =
      type === 'video'
        ? (existingTrack?.isMuted() ?? this.loadMuteStateFromStorage().isVideoMuted)
        : (existingTrack?.isMuted() ?? false);

    // Audio needs its constraints restated. createLocalTracks without a `constraints.audio`
    // object makes lib-jitsi-meet fall back to its own defaults, so a mic switch silently
    // dropped JITSI_OPTIMAL_AUDIO_CONSTRAINTS — most importantly channelCount:1. A stereo
    // capture then breaks the noise suppressor, which only writes channel 0, and remote
    // listeners hear audio on one side only (see NoiseSuppressionEffect.startEffect).
    // Video is deliberately left unconstrained here: the join path starts it on fast-start
    // constraints and upgrades separately, so imposing optimal constraints on a camera
    // switch would diverge from that flow.
    const [newTrack] = await JitsiMeetJS.createLocalTracks({
      devices: [type],
      ...deviceOption,
      ...(type === 'audio' && {
        constraints: { audio: { ...JITSI_OPTIMAL_AUDIO_CONSTRAINTS } },
      }),
    });

    if (existingTrack) {
      // Detach NS via setEffect() before replacing — it restores the live mic track so
      // replaceTrack can find it. Calling nsEffect.stopEffect() directly here instead
      // ends the output track and breaks replaceTrack ("no transceiver for old: null").
      if (type === 'audio' && this.nsEffect) {
        try {
          await existingTrack.setEffect(undefined);
        } catch (e) {
          Logger.error('[JitsiMeetingService] Failed to detach NS before track replace:', e);
        }
        this.nsEffect = null;
      }

      await this.conference.replaceTrack(existingTrack, newTrack);
      const trackIndex = this.localTrackService.tracks.findIndex(
        (t: any) => t.getId() === existingTrack.getId()
      );
      if (trackIndex !== -1) {
        this.localTrackService.tracks[trackIndex] = newTrack;
      }
    } else {
      await this.conference.addTrack(newTrack);
      this.localTrackService.addTracks([newTrack]);
    }

    // Restore the previous mute state on the new track.
    if (wasMuted && !newTrack.isMuted()) {
      await newTrack.mute();
      const trackType = type === 'audio' ? MeetingTrackType.AUDIO : MeetingTrackType.VIDEO;
      this.eventSubject.next(
        this.eventMapper.createTrackMuted(trackType, this.getMyUserId(), newTrack)
      );
    }

    if (type === 'video') {
      // Apply VB in the background so camera starts faster
      void this.applyVirtualBackgroundToLocalVideo();
    } else if (type === 'audio') {
      // Reconcile NS for the new track — lazy, so it applies only if the track is unmuted.
      await this.applyNoiseSuppression();
    }
  }

  async setAudioInputDevice(deviceId: string): Promise<void> {
    try {
      await this.replaceOrAddTrack('audio', deviceId);
    } catch (error) {
      Logger.error('Error setting audio input device:', error);
      throw error;
    }
  }

  async setVideoInputDevice(deviceId: string): Promise<void> {
    try {
      Logger.debug('[deviceId]', deviceId);
      await this.replaceOrAddTrack('video', deviceId);
    } catch (error) {
      Logger.error('Error setting video input device:', error);
      throw error;
    }
  }

  async setAudioOutputDevice(deviceId: string): Promise<void> {
    try {
      await JitsiMeetJS.mediaDevices.setAudioOutputDevice(deviceId);
    } catch (error) {
      Logger.error('Error setting audio output device:', error);
      throw error;
    }
  }

  async destroy(): Promise<void> {
    this.cleanupAudioLevelHandlers();
    this.eventSubject.complete();
    this.audioLevelSubject.complete();
    await this.leaveMeeting({ killConnection: true });
    this.conference = null;
    this.connection = null;
    await this.localTrackService.clearAll();
  }

  // Util methods
  async startScreenShare(onStopped?: () => void): Promise<void> {
    await this.screenShareManager.startScreenShare(this.conference, onStopped);
  }

  async stopScreenShare(): Promise<void> {
    await this.screenShareManager.stopScreenShare(this.conference);
  }

  async toggleScreenShare(): Promise<void> {
    await this.screenShareManager.toggleScreenShare(this.conference);
  }

  async muteAudio(): Promise<void> {
    await this.toggleTrack(MeetingTrackType.AUDIO, true);
  }

  async unmuteAudio(): Promise<void> {
    await this.toggleTrack(MeetingTrackType.AUDIO, false);
  }

  async muteVideo(): Promise<void> {
    await this.toggleTrack(MeetingTrackType.VIDEO, true);
  }

  async unmuteVideo(): Promise<void> {
    await this.toggleTrack(MeetingTrackType.VIDEO, false);
  }

  async toggleRecording(): Promise<void> {
    const { recordingSessionId } = useMeetingStore.getState();
    const { roomRecordingStatus } = useRecordingStore.getState();
    if (recordingSessionId && roomRecordingStatus === 'on') {
      await this.stopMeetingRecording();
    } else {
      await this.startMeetingRecording();
    }
  }

  async startMeetingRecording(): Promise<string | null> {
    const data = await this.conference?.startRecording({ mode: 'file' });
    useLiveCaptionsStore.getState().clearTranscripts();
    useMeetingStore.getState().setRecordingSessionId(data._sessionID);
    return data._sessionID;
  }

  async stopMeetingRecording(): Promise<void> {
    try {
      const { recordingSessionId } = useMeetingStore.getState();
      if (!recordingSessionId) {
        Logger.warn('UNABLE TO STOP RECORDING, NO SESSION ID FOUND');
        return;
      }
      await this.conference?.stopRecording(recordingSessionId);
      useMeetingStore.getState().setRecordingSessionId(null);
    } catch (error) {
      Logger.error(error);
    }
  }

  // Getter methods

  getConnectionState(): MeetingConnectionState {
    return this.eventMapper.getConnectionState();
  }

  getRoomState(): MeetingRoomState {
    return this.eventMapper.getRoomState();
  }

  isConnecting(): boolean {
    return this.getConnectionState() === MeetingConnectionState.CONNECTING;
  }

  isConnected(): boolean {
    return this.getConnectionState() === MeetingConnectionState.CONNECTED;
  }

  isDisconnected(): boolean {
    return this.getConnectionState() === MeetingConnectionState.DISCONNECTED;
  }

  isConnectionFailed(): boolean {
    return this.getConnectionState() === MeetingConnectionState.FAILED;
  }

  private emitInitialCameraState(): void {
    const videoTrack = this.localTracks.find((t: any) => t.getType() === 'video');
    let newState: boolean;

    if (videoTrack) {
      // Track exists — use its actual mute state
      newState = !videoTrack.isMuted();
    } else {
      // No track: only trust storage if video was intentionally skipped due to mute.
      // If storage itself says muted, we know the skip was deliberate → camera off.
      // If storage says unmuted but no track exists, we have no reliable evidence — skip emit.
      const { isVideoMuted } = this.loadMuteStateFromStorage();
      if (!isVideoMuted) return;
      newState = false;
    }

    if (newState !== this.cameraIsOn) {
      this.handleCameraActivityChange(newState ? 'on' : 'off');
    }
  }

  /**
   * Handle camera activity change and emit tracking event
   */
  private handleCameraActivityChange(status: 'on' | 'off'): void {
    try {
      this.cameraIsOn = status === 'on';
      MeetingService.eventRouter.emitCameraActivityEvent(status);
    } catch (error) {
      Logger.error('[JitsiMeetingService] Error handling camera activity change:', error);
    }
  }

  /**
   * Disposing local tracks on leave (see leaveMeeting()) fires TRACK_REMOVED,
   * not TRACK_MUTE_CHANGED — the event onTrackMuteChanged listens for — so a
   * camera/mic left "on" at leave time never got a matching "off" activity
   * event, and camera_seconds/mic_seconds kept growing forever on every
   * rollup pass afterward. This flushes the missing "off" explicitly before
   * tracks are torn down, mirroring what the mute toggle button already does.
   */
  private flushCameraMicOffOnLeave(): void {
    if (this.cameraIsOn) {
      this.handleCameraActivityChange('off');
    }
    if (this.micIsOn) {
      this.handleMicActivityChange('off');
    }
  }

  /**
   * NGE-841 — mirrors emitInitialCameraState() for the local audio track.
   */
  private emitInitialMicState(): void {
    const audioTrack = this.localTracks.find((t: any) => t.getType() === 'audio');
    if (!audioTrack) return;

    const newState = !audioTrack.isMuted();
    if (newState !== this.micIsOn) {
      this.handleMicActivityChange(newState ? 'on' : 'off');
    }
  }

  /**
   * NGE-841 — mirrors handleCameraActivityChange() for the local mic.
   */
  private handleMicActivityChange(status: 'on' | 'off'): void {
    try {
      this.micIsOn = status === 'on';
      MeetingService.eventRouter.emitMicActivityEvent(status);
    } catch (error) {
      Logger.error('[JitsiMeetingService] Error handling mic activity change:', error);
    }
  }

  /**
   * Track participant activity (DEPRECATED - no longer sent to backend)
   * Activity tracking was removed as it's no longer needed
   */
  private async trackParticipantActivity(
    _participantId: string,
    _activityType: 'speaking' | 'joined' | 'left' | 'muted' | 'unmuted',
    _metadata?: { audioLevel?: number; duration?: number }
  ): Promise<void> {
    // DEPRECATED: Activity tracking endpoint has been removed
    // Keeping method signature for backward compatibility but doing nothing
    return;
  }

  // Room Switching Methods

  /**
   * Parse room JID to extract the room identifier
   */
  private parseRoomJid(originalRoomJid: string): string {
    if (!originalRoomJid.includes('@breakout')) {
      const [roomIdentifier] = originalRoomJid.split('@');
      return roomIdentifier;
    }
    return originalRoomJid;
  }

  /**
   * Leave the conference only, keeping the connection alive
   * This is a lighter-weight version of leaveMeeting for room switching
   */
  private async leaveConferenceOnly(leaveReason: string): Promise<void> {
    // Dispose virtual background effect
    try {
      const videoTrack = await this.getLocalVideoTrack();
      if (videoTrack) await videoTrack.setEffect(undefined);
    } catch (error) {
      console.warn('[JitsiMeetingService] Error clearing video effect:', error);
    }

    // Dispose noise suppression effect
    try {
      const audioTrack = this.getLocalAudioTrack();
      if (audioTrack) await audioTrack.setEffect(undefined);
    } catch (error) {
      console.warn('[JitsiMeetingService] Error clearing audio effect:', error);
    }
    this.nsEffect?.stopEffect();
    this.nsEffect = null;

    // Use LocalTrackService to preserve screen share if switching rooms
    const typesToKeep = this.isRoomSwitching ? [MeetingTrackType.SCREEN] : [];
    await this.localTrackService.disposeTracks(typesToKeep);

    if (!this.conference) {
      return;
    }

    // Remove event listeners
    this.removeConferenceEventListeners();
    this.cleanupAudioLevelHandlers();

    // Leave the conference
    try {
      await this.conference.leave(leaveReason);
    } catch (error) {
      console.error('[JitsiMeetingService] Error leaving conference:', error);
    } finally {
      // Always mark as LEFT so callers (e.g. GuestLobbyPage) can proceed with re-join.
      this.eventMapper.setRoomState(MeetingRoomState.LEFT);
      this.conference = null;
      this.eventMapper.setRoomName('');
    }
  }

  /**
   * Wait for the conference to be fully cleaned up
   * Uses a combination of timeout and checking connection state
   */
  private async waitForConferenceCleanup(): Promise<void> {
    const maxWaitTime = 3000;
    const checkInterval = 100;
    const startTime = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        // Conference is null and connection is still alive
        if (!this.conference && this.connection) {
          // Add a small buffer for server-side cleanup
          setTimeout(resolve, 500);
          return;
        }

        if (Date.now() - startTime >= maxWaitTime) {
          console.warn('[JitsiMeetingService] Conference cleanup timeout, proceeding anyway');
          resolve();
          return;
        }

        setTimeout(check, checkInterval);
      };

      check();
    });
  }

  async getMainRoom(): Promise<string> {
    const maxAttempts = 20;
    const intervalMs = 250;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (this.conference && typeof this.conference.getBreakoutRooms === 'function') {
        const mainRoomJid = this.conference.getBreakoutRooms()._mainRoomJid;
        if (mainRoomJid) {
          return mainRoomJid;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Timeout waiting for conference and breakout rooms to be available');
  }

  /**
   * Switch to a different room (breakout or main)
   * Keeps the connection alive and handles the transition cleanly
   */
  private async waitForConnection(retryCount: number = 10, delayMs: number = 1000): Promise<void> {
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      // this.connection is assigned synchronously in initialize(), BEFORE
      // `await this.connection.connect()` resolves — so checking it alone returns
      // almost immediately even while the XMPP handshake is still in flight. Right
      // after a page refresh, a breakout-room switch can be requested (participant
      // assignment detected) within that same tight window as the bootstrap
      // connection is still establishing, so this used to let switchToRoom send a
      // conference-join IQ over a stream that wasn't ready yet — Prosody/Jicofo
      // reject it as "Not connected"/"session-invalid"/"not-acceptable", all of
      // which exhaust joinConferenceWithRetry's retries and strand the participant
      // with no conference at all. isConnected() reflects the actual
      // CONNECTION_ESTABLISHED event instead of just object existence.
      if (this.isConnected()) {
        Logger.debug(`[JitsiMeetingService] Connection ready on attempt ${attempt}`);
        return;
      }

      Logger.debug(`[JitsiMeetingService] Connection not ready, attempt ${attempt}/${retryCount}`);

      if (attempt < retryCount) {
        await new Promise((res) => setTimeout(res, delayMs));
      }
    }

    throw new Error(`Timeout: connection not established after ${retryCount} attempts`);
  }

  /**
   * Remove all conference event listeners
   */
  private removeConferenceEventListeners(): void {
    if (!this.conference) return;

    const {
      handleRecorderStateChanged,
      handleBreakoutRoomStateChanged,
      handleMoveToBreakoutRoom,
      handleConferenceError,
    } = this.eventMapper;

    this.conference.off(JitsiConferenceEvents.TRACK_ADDED, this.onTrackAdded);
    this.conference.off(JitsiConferenceEvents.TRACK_REMOVED, this.onTrackRemoved);
    this.conference.off(JitsiConferenceEvents.TRACK_MUTE_CHANGED, this.onTrackMuteChanged);
    this.conference.off(JitsiConferenceEvents.CONFERENCE_JOINED, this.onConferenceJoined);
    this.conference.off(JitsiConferenceEvents.USER_JOINED, this.onUserJoined);
    this.conference.off(JitsiConferenceEvents.USER_LEFT, this.onUserLeft);
    this.conference.off(JitsiConferenceEvents.USER_ROLE_CHANGED, this.onUserRoleChanged);
    this.conference.off(JitsiConferenceEvents.CONFERENCE_ERROR, handleConferenceError);
    this.conference.off(JitsiConferenceEvents.RECORDER_STATE_CHANGED, handleRecorderStateChanged);
    this.conference.off(
      JitsiConferenceEvents.BREAKOUT_ROOMS_UPDATED,
      handleBreakoutRoomStateChanged
    );
    this.conference.off(
      JitsiConferenceEvents.BREAKOUT_ROOMS_MOVE_TO_ROOM,
      handleMoveToBreakoutRoom
    );
  }

  // Latest room requested while a switch was already in flight. A remount of the
  // breakout panel (e.g. AppController closing/reopening it on session reload) can
  // call switchToRoom again before the first switch finishes; without queuing, that
  // second request used to be silently dropped and never retried, leaving the
  // participant stuck in their previous room while the store thought they'd moved.
  private nextRequestedRoomJid: string | null = null;

  async switchToRoom(targetRoomJid: string): Promise<void> {
    if (!targetRoomJid) {
      console.warn('[JitsiMeetingService] switchToRoom called with empty roomJid');
      return;
    }

    if (this.isRoomSwitching) {
      Logger.debug(
        '[JitsiMeetingService] Room switch already in progress, queuing:',
        targetRoomJid
      );
      this.nextRequestedRoomJid = targetRoomJid;
      return;
    }

    // Run behind the same queue as joinMeeting so a concurrent bootstrap join
    // (e.g. the initial main-room join on page reload) can never race this call
    // for ownership of this.conference/this.connection.
    return this.runExclusive(() => this.switchToRoomWithRetry(targetRoomJid));
  }

  /** Retries the whole leave+join cycle a few times before giving up, since a single
   * failed attempt used to leave the client with this.conference === null forever —
   * not in the target room, not back in the previous room, with no self-recovery. */
  private async switchToRoomWithRetry(targetRoomJid: string, maxAttempts = 3): Promise<void> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await this.switchToRoomInternal(targetRoomJid);
        return;
      } catch (error) {
        lastError = error;
        Logger.warn(
          `[JitsiMeetingService] switchToRoom attempt ${attempt}/${maxAttempts} failed:`,
          error
        );
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    Logger.error('[JitsiMeetingService] switchToRoom exhausted all retries, giving up:', lastError);
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  private async switchToRoomInternal(targetRoomJid: string): Promise<void> {
    // Wait for connection to be initialized (with retry)
    try {
      await this.waitForConnection();
    } catch (error) {
      console.error('[JitsiMeetingService] Connection not available:', error);
      throw error;
    }

    // Parse the room JID
    const roomJid = this.parseRoomJid(targetRoomJid);
    // initJitsiConference() is always called with only the part before '@' (see
    // joinMeetingInternal), so conference.getName() never includes a domain — even
    // for breakout rooms, whose roomJid keeps '@breakout.8x8.vc'. Comparisons must
    // use the identifier-only form on both sides, or "already in room"/"did the
    // join land" checks silently never match and force pointless leave+rejoin loops.
    const roomIdentifier = roomJid.split('@')[0];
    const currentRoomName = this.conference?.getName();

    // Check if we're already in this room
    if (currentRoomName && currentRoomName === roomIdentifier) {
      Logger.debug('[JitsiMeetingService] Already in room:', roomJid);
      return;
    }

    Logger.debug('[JitsiMeetingService] Switching from', currentRoomName, 'to', roomJid);

    try {
      this.isRoomSwitching = true;
      this.pendingRoomJid = roomJid;
      this.previousRoomJid = currentRoomName || null;
      // Persist current mute state to localStorage before leaving
      this.saveMuteStateToStorage(this.getLocalMuteState());

      // Opens the participants grid's skeleton window for the room being entered (see
      // useParticipantSkeletonCells). Set here rather than in the callers because this is the
      // single funnel every room switch goes through — breakout entry/exit included.
      useMeetingStore.setState({ roomJoinStartedAt: Date.now() });

      // Emit room switch started event
      this.eventSubject.next({
        type: 'ROOM_SWITCH_STARTED',
        payload: {
          fromRoom: currentRoomName,
          toRoom: roomJid,
        },
      });

      // Drop every remote participant tile from the old room now, since
      // leaveConferenceOnly() removes USER_LEFT listeners before actually
      // leaving, so those participants would otherwise never get pruned and
      // would keep rendering as if present in the room we're switching into.
      // The new conference's USER_JOINED events will repopulate whoever is
      // actually in the target room.
      const myId = this.getMyUserId();
      useMeetingStore.setState((state) => {
        const jitsiParticipants: typeof state.jitsiParticipants = {};
        if (myId && state.jitsiParticipants[myId]) {
          jitsiParticipants[myId] = state.jitsiParticipants[myId];
        }
        return { jitsiParticipants };
      });

      // Leave current conference - keep connection alive
      await this.leaveConferenceOnly(CONFERENCE_LEAVE_REASONS.SWITCH_ROOM);

      // Wait for the leave to fully propagate
      await this.waitForConferenceCleanup();

      // Join the new room (initLocalTracks reads mute state from localStorage).
      // Calls the internal, non-queued join — we're already running inside
      // runExclusive here, so going through the public joinMeeting() would deadlock.
      await this.joinMeetingInternal(roomJid, {
        createLocalTracks: true,
        tracks: { types: [MeetingTrackType.AUDIO, MeetingTrackType.VIDEO] },
      });

      // joinMeetingInternal swallows its own errors (emits an event, never throws),
      // so verify the join actually landed on the target room instead of trusting
      // the promise resolving — otherwise a failed join looks identical to success
      // here and switchToRoomWithRetry would never know to retry. Compare against
      // the identifier only (see roomIdentifier comment above) — conference.getName()
      // never includes the '@breakout.8x8.vc' domain.
      if (this.conference?.getName() !== roomIdentifier) {
        throw new Error(
          `switchToRoom: join did not land on target room ${roomJid} (conference is ${this.conference?.getName() ?? 'null'})`
        );
      }

      // Emit room switch completed event
      this.eventSubject.next({
        type: 'ROOM_SWITCH_COMPLETED',
        payload: {
          fromRoom: this.previousRoomJid,
          toRoom: roomJid,
        },
      });
    } catch (error) {
      console.error('[JitsiMeetingService] Room switch failed:', error);
      this.eventMapper.setRoomState(MeetingRoomState.ERROR);

      // Emit room switch failed event
      this.eventSubject.next({
        type: 'ROOM_SWITCH_FAILED',
        payload: {
          fromRoom: this.previousRoomJid,
          toRoom: roomJid,
          error,
        },
      });

      throw error;
    } finally {
      this.isRoomSwitching = false;
      this.pendingRoomJid = null;

      // A newer switch was requested while this one was in flight — replay it now
      // instead of dropping it. Skip if it already matches where we just landed
      // (compare identifiers only — conference.getName() never has the domain).
      const next = this.nextRequestedRoomJid;
      this.nextRequestedRoomJid = null;
      if (next && next.split('@')[0] !== this.conference?.getName()) {
        void this.switchToRoom(next);
      }
    }
  }

  /**
   * Check if a room switch is currently in progress
   */
  isRoomSwitchInProgress(): boolean {
    return this.isRoomSwitching;
  }

  /**
   * Get the previous room JID (useful for returning to a room after switching)
   */
  getPreviousRoomJid(): string | null {
    return this.previousRoomJid;
  }
}

function createEnhancedConstraints(userConstraints?: any): any {
  const enhancedConstraints = {
    audio: { ...JITSI_OPTIMAL_AUDIO_CONSTRAINTS },
    video: { ...JITSI_OPTIMAL_VIDEO_CONSTRAINTS },
  };

  if (userConstraints) {
    if (userConstraints.audio) {
      enhancedConstraints.audio = {
        ...enhancedConstraints.audio,
        ...userConstraints.audio,
      };
    }

    if (userConstraints.video) {
      enhancedConstraints.video = {
        ...enhancedConstraints.video,
        ...userConstraints.video,
      };
    }
  }

  return enhancedConstraints;
}

function createOptimizedTrackOptions(devices: string[], constraints?: any): any {
  return {
    devices,
    constraints: createEnhancedConstraints(constraints),
    ...JITSI_OPTIMAL_BITRATE_CONFIG,
    videoQuality: JITSI_OPTIMAL_VIDEO_QUALITY,
  };
}
